<template>
  <div class="student-manage">
    <el-card ref="searchCard" shadow="never" class="search-card">
      <div class="search-area">
        <el-form :inline=true :model="searchForm" class="search-form" label-width="80px">
          <el-form-item label="学员姓名">
            <el-input v-model="searchForm.studentName" placeholder="请输入学员姓名" clearable />
          </el-form-item>
          <el-form-item label="来源渠道">
            <el-select v-model="searchForm.sourceCode" placeholder="请选择" clearable>
              <el-option v-for="item in sourceOptions" :key="item.code" :label="item.name" :value="item.code" />
            </el-select>
          </el-form-item>
          <el-form-item label="手机号">
            <el-input v-model="searchForm.phoneNumber" placeholder="请输入手机号" clearable />
          </el-form-item>
          <el-form-item label="咨询课程">
            <el-cascader
              v-model="searchForm.projectCodes"
              :options="projectCascaderOptions"
              :props="{ expandTrigger: 'hover', checkStrictly: true }"
              placeholder="请选择"
              clearable
              filterable
              style="width: 200px;"
            />
          </el-form-item>
          <el-form-item label="推荐机构">
            <utwl-org-select
              v-model="searchForm.orgId"
              placeholder="请输入机构名称搜索"
            />
          </el-form-item>
          <el-form-item label="报名机构">
            <utwl-org-select
              v-model="searchForm.enrolledOrgId"
              placeholder="请输入机构名称搜索"
              error-message="加载报名机构失败"
            />
          </el-form-item>
          <el-form-item label="是否有效">
            <el-select v-model="searchForm.validStatusCode" placeholder="请选择" clearable>
              <el-option v-for="item in validStatusOptions" :key="item.code" :label="item.name" :value="item.code" />
            </el-select>
          </el-form-item>
          <el-form-item label="推荐人">
            <el-select
              v-model="searchForm.referrerStaffId"
              placeholder="请选择"
              popper-class="org-select-popper"
              filterable
              remote
              clearable
              :remote-method="onSearchReferrerStaff"
              :loading="filterReferrerStaffLoading"
              @visible-change="onSearchReferrerDropdownVisibleChange"
            >
              <el-option
                v-for="staff in filterReferrerStaffOptions"
                :key="staff.staffId"
                :label="formatReferrerStaffOptionLabel(staff)"
                :value="staff.staffId"
              >
                <span class="org-option-text" :title="formatReferrerStaffOptionLabel(staff)">{{ formatReferrerStaffOptionLabel(staff) }}</span>
              </el-option>
              <div class="org-select-pager" @mousedown.prevent @click.stop>
                <el-button
                  size="mini"
                  :disabled="filterReferrerStaffPage <= 1 || filterReferrerStaffLoading"
                  @click.stop="changeSearchReferrerPage(filterReferrerStaffPage - 1)"
                >上一页</el-button>
                <span class="org-select-pager__text">第 {{ filterReferrerStaffPage }} / {{ filterReferrerStaffTotalPages }} 页（每页10条）</span>
                <el-button
                  size="mini"
                  :disabled="filterReferrerStaffPage >= filterReferrerStaffTotalPages || filterReferrerStaffLoading"
                  @click.stop="changeSearchReferrerPage(filterReferrerStaffPage + 1)"
                >下一页</el-button>
              </div>
            </el-select>
          </el-form-item>
          <el-form-item label="咨询时间">
            <el-date-picker
              v-model="searchForm.consultTimeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              clearable
              style="width: 360px;"
            />
          </el-form-item>
          <el-form-item class="operate_btn_box"> 
            <div class="left_box">
              <el-form-item>
                <el-button type="primary" icon="el-icon-search" @click="handleSearch" :loading="searchLoading">查询</el-button>
                <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
                <el-button v-if="canShowStudentWriteActions" icon="el-icon-plus" @click="handleAdd">新增学生</el-button>
                <el-button v-if="isAdmin" icon="el-icon-download" @click="handleExport" :loading="exportLoading">导出</el-button>
                <el-button v-if="canShowManageListDelete" icon="el-icon-delete" :disabled="!selectedRows.length" @click="handleBatchDelete" :loading="batchDeleteLoading">批量删除</el-button>
              </el-form-item>
            </div>
            <div class="right_box">
              <el-radio-group v-model="viewMode" @change="handleViewModeChange">
                <el-radio-button label="list">列表模式</el-radio-button>
                <el-radio-button label="card">卡片模式</el-radio-button>
              </el-radio-group>
            </div>
          </el-form-item>
        </el-form>
      </div>
    </el-card>

    <el-card ref="tableCard" shadow="never" class="table-card">
      <el-table v-if="viewMode === 'list'" ref="studentListTable" :height="studentTableHeight" :key="tableColumnRenderKey" :data="tableData" v-loading="loading" border stripe @selection-change="handleSelectionChange" @sort-change="handleSortChange" :default-sort="{ prop: 'createTime', order: 'descending' }">
        <el-table-column type="selection" width="45" :selectable="studentRowSelectable" />
        <el-table-column type="index" label="序号" width="55" :index="indexMethod" align="center"/>
        <template v-for="col in activeTableColumns">
          <el-table-column
            v-if="col.key === 'studentName'"
            :key="`${col.key}-studentName`"
            :label="col.label"
            :min-width="col.minWidth"
            show-overflow-tooltip
          >
            <template slot-scope="{ row }">
              <span>
                {{ row.studentName }}
                （<span :class="row.pushStatusCode === 'PUSHED' ? 'student-push-tag-pushed' : 'student-push-tag-unpushed'">
                  {{ row.pushStatusCode === 'PUSHED' ? '已推送' : '未推送' }}
                </span>
                ）
              </span>
            </template>
          </el-table-column>

          <el-table-column v-else-if="col.key === 'consultCourse'" :key="`${col.key}-consultCourse`" :label="col.label" :min-width="col.minWidth" :width="col.width" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ formatProjectDisplay(row) }}
            </template>
          </el-table-column>

          <el-table-column v-else-if="col.key === 'cityDisplay'" :key="`${col.key}-cityDisplay`" :label="col.label" :min-width="col.minWidth" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ formatCityDisplay(row) }}
            </template>
          </el-table-column>

          <el-table-column v-else-if="col.key === 'validStatus'" :key="`${col.key}-validStatus`" :label="col.label" :min-width="col.minWidth">
            <template slot-scope="{ row }">
              <el-tag :type="getValidStatusType(row.validStatusCode)" size="small">{{ row.validStatusName || row.validStatusCode }}</el-tag>
            </template>
          </el-table-column>

          <el-table-column v-else-if="col.key === 'referrerName'" :key="`${col.key}-referrerName`" :label="col.label" :min-width="col.minWidth" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ row.referrerName || '-' }}
            </template>
          </el-table-column>

          <el-table-column v-else-if="col.key === 'phoneNumber'" :key="`${col.key}-phoneNumber`" :prop="col.prop" :label="col.label" :width="col.width" :min-width="col.minWidth" :align="col.align" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ maskPhoneForTable(row.phoneNumber) }}
            </template>
          </el-table-column>

          <el-table-column v-else-if="col.key === 'countdown'" :key="`${col.key}-countdown`" :prop="col.prop" :label="col.label" :min-width="col.minWidth" sortable="custom">
            <template slot-scope="{ row }">
              <span :class="getCountdownClass(row)">
                {{ formatCountdown(row) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column v-else-if="col.key === 'createTime'" align="center" :key="`${col.key}-createTime`" :prop="col.prop" :label="col.label" :min-width="col.minWidth" sortable="custom">
            <template slot-scope="{ row }">
              <div class="time-cell">
                <div>{{ row.createTime ? row.createTime.split(' ')[0] : '-' }}</div>
                <div class="time-part">{{ row.createTime ? row.createTime.split(' ')[1] : '' }}</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column v-else :key="`${col.key}-default`" :prop="col.prop" :label="col.label" :width="col.width" :min-width="col.minWidth" show-overflow-tooltip>
            <template slot-scope="{ row }">
              {{ row[col.prop] || '-' }}
            </template>
          </el-table-column>
        </template>
        <el-table-column label="操作" width="140" fixed="right">
          <template slot="header">
            <div class="operation-header-cell">
              <span>操作</span>
              <el-button type="text" icon="el-icon-s-tools" class="column-config-trigger" @click.stop="openColumnConfigDialog" />
            </div>
          </template>
          <template slot-scope="{ row }">
            <el-button type="text" @click="handleDetail(row)">详情</el-button>
            <el-button
              type="text"
              :disabled="!isPrivilegedStudentEditor && (row.validStatusCode === 'INVALID')"
              @click="handleEdit(row)"
            >编辑</el-button>
            <el-button v-if="canShowManageListDelete" type="text" class="danger-btn" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-else class="student-card-wrapper" v-loading="loading">
        <el-row :gutter="16">
          <el-col v-for="row in tableData" :key="row.id" :span="8">
            <el-card shadow="never" :class="['student-card-item', { 'student-card-item-unpushed': row.pushStatusCode !== 'PUSHED' }]">
              <span :class="['student-card-status', row.pushStatusCode === 'PUSHED' ? 'student-card-status-pushed' : 'student-card-status-unpushed']">
                {{ row.pushStatusCode === 'PUSHED' ? '已推送' : '未推送' }}
              </span>
              <div class="student-card-line"><span class="student-card-label">学员姓名</span><span class="student-card-value">{{ row.studentName || '-' }}</span></div>
              <div class="student-card-line"><span class="student-card-label">手机号</span><span class="student-card-value">{{ row.phoneNumber || '-' }}</span></div>
              <div class="student-card-line"><span class="student-card-label">所在城市</span><span class="student-card-value">{{ formatCityDisplay(row) }}</span></div>
              <div class="student-card-line"><span class="student-card-label">咨询课程</span><span class="student-card-value">{{ formatProjectDisplay(row) }}</span></div>
              <div class="student-card-line">
                <span class="student-card-label">是否有效</span>
                <span class="student-card-value">
                  <el-tag :type="getValidStatusType(row.validStatusCode)" size="mini">
                    {{ row.validStatusName || row.validStatusCode || '-' }}
                  </el-tag>
                </span>
              </div>
              <div class="student-card-line"><span class="student-card-label">推荐人</span><span class="student-card-value">{{ row.referrerName || '-' }}</span></div>
              <div class="student-card-line"><span class="student-card-label">跟进人</span><span class="student-card-value">{{ row.followerName || '-' }}</span></div>
              <div class="student-card-time-line">咨询时间：{{ row.createTime || '-' }}</div>
            </el-card>
          </el-col>
        </el-row>
        <el-empty v-if="!loading && (!tableData || tableData.length === 0)" description="暂无数据" />
      </div>
    </el-card>

    <div ref="paginationWrapper" class="pagination-wrapper">
      <el-pagination
        class="pagination"
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        :page-size="pageSize"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="960px" :close-on-click-modal="false" @close="resetForm">
      <el-form :model="form" :rules="rules" ref="form" label-width="90px" class="student-form">
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="学员姓名" prop="studentName">
              <el-input v-model="form.studentName" placeholder="请输入学员姓名" style="width: 100%;" clearable />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="所在城市" prop="cityCode">
              <region-cascader
                ref="regionCascader"
                v-model="form.cityCode"
                placeholder="请选择省市区"
                width="100%"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="来源渠道" prop="sourceCode">
              <el-select v-model="form.sourceCode" placeholder="请选择来源渠道" style="width: 100%;">
                <el-option v-for="item in sourceOptions" :key="item.code" :label="item.name" :value="item.code" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="推荐人">
              <el-input v-if="form.referrerStaffId" v-model="form.referrerName" style="width: 100%;" disabled />
              <el-select v-else v-model="form.referrerStaffId" placeholder="请选择推荐人（会员）" filterable clearable style="width: 100%;" :loading="referrerStaffLoading">
                <el-option v-for="s in referrerStaffOptions" :key="s.staffId" :label="`${s.staffName || s.username} (${s.phoneNumber})`" :value="s.staffId" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="手机号" prop="contacts" required>
          <div v-for="(item, index) in form.contacts" :key="index" class="student-contact-row">
            <el-row :gutter="12" type="flex" align="middle">
              <el-col :span="10">
                <div class="contact-input-wrap">
                  <el-input
                    v-model="item.phone"
                    placeholder="请输入手机号"
                    clearable
                    style="width: 100%;"
                    @blur="onContactPhoneBlur(item.phone)"
                  />
                </div>
              </el-col>
              <el-col :span="10">
                <div class="contact-input-wrap">
                  <span class="contact-inline-label contact-inline-label-wechat is-required">微信号</span>
                  <el-input v-model="item.wechat" placeholder="请输入微信号" clearable />
                </div>
              </el-col>
              <el-col :span="4" class="student-contact-actions-col">
                <div class="student-contact-actions">
                  <i
                    v-if="form.contacts.length < 3 && index === form.contacts.length - 1"
                    class="el-icon-circle-plus-outline student-contact-add"
                    title="新增"
                    @click="addContactRow"
                  ></i>
                  <i
                    class="el-icon-remove-outline student-contact-remove"
                    :class="{ disabled: form.contacts.length <= 1 }"
                    title="删除"
                    @click="form.contacts.length > 1 && removeContactRow(index)"
                  ></i>
                </div>
              </el-col>
            </el-row>
          </div>
        </el-form-item>
        <el-form-item label="咨询课程" prop="projectCodes" class="project-form-item">
          <div class="project-checkbox-container">
            <div v-for="category in projectCascaderOptions" :key="category.value" class="project-category">
              <div class="category-title">{{ category.label }}</div>
              <div class="category-items">
                <el-checkbox-group v-model="form.projectCodes">
                  <el-checkbox 
                    v-for="item in category.children" 
                    :key="item.value" 
                    :label="item.value"
                  >{{ item.label }}</el-checkbox>
                </el-checkbox-group>
              </div>
            </div>
          </div>
        </el-form-item>
        <el-form-item label="详细地址" prop="addressDetail">
          <el-input 
            type="textarea" 
            v-model="form.addressDetail" 
            :rows="2" 
            placeholder="请输入详细地址（最多200字）" 
            maxlength="200" 
            show-word-limit 
            style="width: 100%;"
          />
        </el-form-item>
        <el-form-item label="备注">
          <el-input 
            type="textarea" 
            v-model="form.remark" 
            :rows="3" 
            placeholder="请输入备注（最多500字）" 
            maxlength="500" 
            show-word-limit 
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </span>
    </el-dialog>

    <!-- 详情弹窗 -->
    <el-dialog title="学生详情" :visible.sync="detailVisible" width="1100px" :close-on-click-modal="false" @close="closeDetail" class="detail-dialog">
      <div class="detail-content" v-if="detailData">
        <el-descriptions :title="basicInfoTitle" :column="3" border class="student-basic-desc">
          <el-descriptions-item label="学员姓名">{{ detailData.studentName }}</el-descriptions-item>
          <el-descriptions-item label="电话">{{ detailData.phoneNumber }}</el-descriptions-item>
          <el-descriptions-item label="项目">{{ detailData.projectCodesNames || formatProjectDisplay(detailData) }}</el-descriptions-item>
          <el-descriptions-item label="地区">{{ formatCityDisplay(detailData) }}</el-descriptions-item>
          <el-descriptions-item label="推荐人">{{ detailData.referrerName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="创建时间">{{ detailData.createTime }}</el-descriptions-item>
          <el-descriptions-item label="地址" :span="3">{{ detailData.addressDetail || '-' }}</el-descriptions-item>
          <el-descriptions-item label="备注" :span="3">{{ detailData.remark || '-' }}</el-descriptions-item>
        </el-descriptions>

        <div class="student-detail-tabs-wrap">
          <el-tabs v-model="activeTab" class="student-detail-tabs" @tab-click="handleDetailTabClick">
            <el-tab-pane label="推送记录" name="push">
              <div class="detail-tab-toolbar">
                <div class="detail-tab-actions">
                  <template v-if="canShowStudentWriteActions">
                    <el-button
                      type="primary"
                      size="small"
                      @click="showPushForm"
                      :disabled="!followRecords || followRecords.length === 0 || isDetailStudentInvalid"
                    >添加推送记录</el-button>
                    <span v-if="isDetailStudentInvalid" class="push-record-tip">学员已无效，无法添加推送记录</span>
                    <span v-else-if="!followRecords || followRecords.length === 0" class="push-record-tip">请先添加跟进记录</span>
                  </template>
                  <div class="detail-tab-view-switch">
                    <el-radio-group v-model="detailRecordsDisplayMode" size="small" @change="persistDetailRecordsDisplayMode">
                      <el-radio-button label="list">列表模式</el-radio-button>
                      <el-radio-button label="timeline">时间线</el-radio-button>
                    </el-radio-group>
                  </div>
                </div>
              </div>
              <el-table
                v-show="detailRecordsDisplayMode === 'list'"
                :data="pushRecords"
                border
                stripe
                align="center"
                size="small"
                class="push-records-table"
              >
                <el-table-column type="index" label="序号" width="60" align="center" />
                <el-table-column prop="orderNo" label="订单编号" min-width="180" show-overflow-tooltip />
                <el-table-column prop="pushTime" label="推送时间" width="160" />
                <el-table-column prop="orgName" label="推荐机构" width="150" />
                <el-table-column prop="orgCampus" label="机构校区" width="120" />
                <el-table-column prop="matchProject" label="匹配项目" width="120" />
                <el-table-column prop="submitMethodName" label="提交方式" width="100" />
                <el-table-column prop="pushUserName" label="推送人" width="100" />
                <el-table-column prop="orderStatusName" label="状态" width="110">
                  <template slot-scope="scope">
                    <span>{{ scope.row.orderStatusName || scope.row.orderStatusCode || '—' }}</span>
                  </template>
                </el-table-column>
                <el-table-column prop="payAmount" label="成交金额" width="100" class-name="push-records-money-cell" />
                <el-table-column prop="commissionAmount" label="返佣" width="100" class-name="push-records-money-cell" />
                <el-table-column prop="refundAmount" label="退费金额" width="100" class-name="push-records-money-cell" />
                <el-table-column prop="refundCommissionAmount" label="退还返佣" width="100" class-name="push-records-money-cell" />
                <el-table-column prop="projectRemark" label="备注" min-width="160" show-overflow-tooltip />
                <el-table-column label="操作" width="88" align="center">
                  <template slot-scope="scope">
                    <el-button
                      type="text"
                      size="small"
                      :disabled="isDetailStudentInvalid"
                      @click="showPushEditForm(scope.row)"
                    >编辑</el-button>
                  </template>
                </el-table-column>
              </el-table>
              <div v-show="detailRecordsDisplayMode === 'timeline'" class="apple-timeline-wrap">
                <el-empty v-if="!pushRecords.length" description="暂无推送记录" :image-size="72" />
                <div v-else class="apple-timeline">
                  <div v-for="(item, idx) in pushRecords" :key="item.id || item.pushTime + idx" class="apple-timeline-row">
                    <div class="apple-timeline-axis">
                      <div :class="['apple-timeline-dot', { 'apple-timeline-dot--latest': idx === 0 }]" />
                      <div v-if="idx < pushRecords.length - 1" class="apple-timeline-connector" />
                    </div>
                    <div class="apple-timeline-card">
                      <div class="apple-timeline-card__time">{{ item.pushTime || '—' }}</div>
                      <div class="apple-timeline-card__title">{{ item.orgName || '推送记录' }}</div>
                      <el-descriptions
                        class="push-timeline-desc"
                        :column="6"
                        size="medium"
                        :colon="true"
                        :label-style="pushTimelineDescLabelStyle"
                      >
                        <el-descriptions-item label="机构校区" :span="3">{{ item.orgCampus || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="匹配项目" :span="3">{{ item.matchProject || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="提交方式" :span="3">{{ item.submitMethodName || item.submitMethod || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="推送人" :span="3" content-class-name="push-desc-cell--push-user">{{ item.pushUserName || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="状态" :span="3">{{ item.orderStatusName || item.orderStatusCode || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="订单编号" :span="3">{{ item.orderNo || '—' }}</el-descriptions-item>
                        <el-descriptions-item
                          v-if="resolvePushRecordEnrollCode(item) === 'ENROLLED'"
                          label="成交金额"
                          :span="3"
                        >
                          <span class="apple-timeline-kv__money-v">{{ item.payAmount }}</span>
                        </el-descriptions-item>
                        <el-descriptions-item
                          v-if="resolvePushRecordEnrollCode(item) === 'ENROLLED'"
                          label="返佣"
                          :span="3"
                          content-class-name="push-desc-cell--commission"
                        >
                          <span class="apple-timeline-kv__money-v">{{ item.commissionAmount }}</span>
                        </el-descriptions-item>
                        <el-descriptions-item
                          v-if="resolvePushRecordEnrollCode(item) === 'REFUND'"
                          label="退费金额"
                          :span="3"
                        >
                          <span class="apple-timeline-kv__money-v">{{ item.refundAmount }}</span>
                        </el-descriptions-item>
                        <el-descriptions-item
                          v-if="resolvePushRecordEnrollCode(item) === 'REFUND'"
                          label="退还返佣"
                          :span="3"
                        >
                          <span class="apple-timeline-kv__money-v">{{ item.refundCommissionAmount }}</span>
                        </el-descriptions-item>
                        <el-descriptions-item
                          v-if="resolvePushRecordEnrollCode(item) !== 'ENROLLED' && resolvePushRecordEnrollCode(item) !== 'REFUND' && resolvePushRecordEnrollCode(item) !== 'NOT_ENROLLED'"
                          label="成交金额"
                          :span="3"
                        >
                          <span class="apple-timeline-kv__money-v">{{ item.payAmount }}</span>
                        </el-descriptions-item>
                        <el-descriptions-item
                          v-if="resolvePushRecordEnrollCode(item) !== 'ENROLLED' && resolvePushRecordEnrollCode(item) !== 'REFUND' && resolvePushRecordEnrollCode(item) !== 'NOT_ENROLLED'"
                          label="返佣"
                          :span="3"
                          content-class-name="push-desc-cell--commission"
                        >
                          <span class="apple-timeline-kv__money-v">{{ item.commissionAmount }}</span>
                        </el-descriptions-item>
                        <el-descriptions-item
                          v-if="resolvePushRecordEnrollCode(item) !== 'ENROLLED' && resolvePushRecordEnrollCode(item) !== 'NOT_ENROLLED' && resolvePushRecordEnrollCode(item) !== 'REFUND'"
                          label="退费金额"
                          :span="6"
                        >
                          <span class="apple-timeline-kv__money-v">{{ item.refundAmount }}</span>
                        </el-descriptions-item>
                        <el-descriptions-item
                          label="备注"
                          :span="6"
                          content-class-name="push-desc-remark-cell"
                        >
                          {{ item.projectRemark || '—' }}
                        </el-descriptions-item>
                      </el-descriptions>
                    </div>
                  </div>
                </div>
              </div>
            </el-tab-pane>
            <el-tab-pane label="跟进记录" name="follow">
              <div class="detail-tab-toolbar">
                <div class="detail-tab-actions">
                  <template v-if="canShowStudentWriteActions">
                    <el-button type="primary" size="small" @click="showFollowForm" :disabled="isDetailStudentInvalid || isDetailRefundFrozen">添加跟进记录</el-button>
                    <span v-if="isDetailStudentInvalid" class="push-record-tip" style="margin-left: 8px;">学员已无效，无法添加跟进记录</span>
                    <span v-else-if="isDetailRefundFrozen" class="push-record-tip" style="margin-left: 8px;">该学员已退费，已被冻结，请联系管理员操作</span>
                  </template>
                  <div class="detail-tab-view-switch">
                    <el-radio-group v-model="detailRecordsDisplayMode" size="small" @change="persistDetailRecordsDisplayMode">
                      <el-radio-button label="list">列表模式</el-radio-button>
                      <el-radio-button label="timeline">时间线</el-radio-button>
                    </el-radio-group>
                  </div>
                </div>
              </div>
              <el-table
                v-show="detailRecordsDisplayMode === 'list'"
                :data="followRecords"
                border
                stripe
                align="center"
                size="small"
                class="follow-records-table"
              >
                <el-table-column type="index" label="序号" width="60" align="center" />
                <el-table-column prop="followTime" label="跟进时间" width="160" />
                <el-table-column prop="nextContactTime" label="下次联系时间" width="160" />
                <el-table-column label="是否有效" width="100">
                  <template slot-scope="scope">
                    <el-tag v-if="scope.row.validStatusCode" :type="getValidStatusType(scope.row.validStatusCode)" size="small">
                      {{ scope.row.validStatusName || scope.row.validStatusCode }}
                    </el-tag>
                    <span v-else>—</span>
                  </template>
                </el-table-column>
                <el-table-column prop="followContent" label="跟进记录" min-width="200" show-overflow-tooltip align="left"/>
                <el-table-column prop="followUserName" label="跟进人" width="100" />
              </el-table>
              <div v-show="detailRecordsDisplayMode === 'timeline'" class="apple-timeline-wrap">
                <el-empty v-if="!followRecords.length" description="暂无跟进记录" :image-size="72" />
                <div v-else class="apple-timeline">
                  <div v-for="(item, idx) in followRecords" :key="item.id || item.followTime + idx" class="apple-timeline-row">
                    <div class="apple-timeline-axis">
                      <div :class="['apple-timeline-dot', { 'apple-timeline-dot--latest': idx === 0 }]" />
                      <div v-if="idx < followRecords.length - 1" class="apple-timeline-connector" />
                    </div>
                    <div class="apple-timeline-card">
                      <div class="apple-timeline-card__time">{{ item.followTime || '—' }}</div>
                      <div class="apple-timeline-card__title">跟进记录</div>
                      <el-descriptions
                        class="follow-timeline-desc"
                        :column="6"
                        size="medium"
                        :colon="true"
                        :label-style="pushTimelineDescLabelStyle"
                      >
                        <el-descriptions-item label="下次联系" :span="3">{{ item.nextContactTime || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="是否有效" :span="3">
                          <template v-if="item.validStatusCode">
                            <el-tag :type="getValidStatusType(item.validStatusCode)" size="small">{{ item.validStatusName || item.validStatusCode }}</el-tag>
                          </template>
                          <template v-else>—</template>
                        </el-descriptions-item>
                        <el-descriptions-item label="跟进人" :span="6">{{ item.followUserName || '—' }}</el-descriptions-item>
                        <el-descriptions-item label="内容" :span="6" content-class-name="follow-desc-content-cell">
                          {{ item.followContent || '—' }}
                        </el-descriptions-item>
                      </el-descriptions>
                    </div>
                  </div>
                </div>
              </div>
            </el-tab-pane>
            <el-tab-pane label="变更记录" name="history">
              <div class="apple-timeline-wrap">
                <el-empty v-if="!basicChangeLogs.length" description="暂无变更记录" :image-size="72" />
                <div v-else class="apple-timeline">
                  <div v-for="(log, idx) in basicChangeLogs" :key="log.id || log.changedAt + idx" class="apple-timeline-row">
                    <div class="apple-timeline-axis">
                      <div :class="['apple-timeline-dot', { 'apple-timeline-dot--latest': idx === 0 }]" />
                      <div v-if="idx < basicChangeLogs.length - 1" class="apple-timeline-connector" />
                    </div>
                    <div class="apple-timeline-card">
                      <div class="apple-timeline-card__time">{{ log.changedAt || '—' }}</div>
                      <div class="apple-timeline-card__title">基础信息</div>
                      <el-descriptions
                        class="history-timeline-desc"
                        :column="6"
                        size="medium"
                        :colon="true"
                        :label-style="pushTimelineDescLabelStyle"
                      >
                        <el-descriptions-item label="变更人" :span="6">
                          {{ log.changedByName || '—' }}
                        </el-descriptions-item>
                        <el-descriptions-item label="变更内容" :span="6" content-class-name="basic-change-content-cell">
                          <ul class="basic-change-list">
                            <li
                              v-for="(it, j) in (log.items || [])"
                              :key="j"
                            >
                              将【{{ it.label }}】由
                              <span class="basic-change-value-big">{{ formatBasicChangeValue(it.before) }}</span>
                              变更为
                              <span class="basic-change-value-big">{{ formatBasicChangeValue(it.after) }}</span>
                            </li>
                          </ul>
                        </el-descriptions-item>
                      </el-descriptions>
                    </div>
                  </div>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
    </el-dialog>

    <!-- 推送记录表单弹窗（添加 / 编辑） -->
    <el-dialog
      :title="pushFormMode === 'edit' ? '编辑推送记录' : '添加推送记录'"
      :visible.sync="pushFormVisible"
      width="1100px"
      :close-on-click-modal="false"
      append-to-body
      class="push-form-dialog"
      @closed="onPushFormDialogClosed"
    >
      <el-form :model="pushForm" :rules="pushRules" ref="pushForm" label-width="150px">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="机构品牌" prop="orgId">
              <el-select
                v-model="pushForm.orgId"
                placeholder="请输入机构名称搜索"
                style="width: 350px;"
                popper-class="org-select-popper"
                filterable
                remote
                clearable
                :remote-method="onPushOrgSearch"
                :loading="orgOptionsLoading"
                @change="handlePushOrgChange"
                @visible-change="onPushOrgDropdownVisibleChange"
              >
                <el-option
                  v-for="org in orgOptions"
                  :key="org.orgId"
                  :label="formatPushOrgOptionLabel(org)"
                  :value="org.orgId"
                  :disabled="isPushOrgOptionDisabled(org)"
                >
                  <span class="org-option-text" :title="formatPushOrgOptionLabel(org)">{{ formatPushOrgOptionLabel(org) }}</span>
                </el-option>
                <div class="org-select-pager" @mousedown.prevent @click.stop>
                  <el-button
                    size="mini"
                    :disabled="orgQueryPage <= 1 || orgOptionsLoading"
                    @click.stop="changeOrgPage(orgQueryPage - 1)"
                  >上一页</el-button>
                  <span class="org-select-pager__text">第 {{ orgQueryPage }} / {{ orgQueryTotalPages }} 页（每页10条）</span>
                  <el-button
                    size="mini"
                    :disabled="orgQueryPage >= orgQueryTotalPages || orgOptionsLoading"
                    @click.stop="changeOrgPage(orgQueryPage + 1)"
                  >下一页</el-button>
                </div>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="机构校区" prop="orgCampus">
              <el-select
                v-model="pushForm.orgCampus"
                :placeholder="pushForm.orgId ? '请选择机构校区' : '请先选择机构品牌'"
                style="width: 350px;"
                clearable
                filterable
                :disabled="!pushForm.orgId"
                @change="handlePushCampusChange"
              >
                <el-option
                  v-for="(opt, idx) in pushCampusOptions"
                  :key="`${opt.campusName}-${opt.statusCode}-${idx}`"
                  :label="formatPushCampusOptionLabel(opt)"
                  :value="opt.campusName"
                  :disabled="isPushCampusOptionDisabled(opt)"
                >
                  <span :title="formatPushCampusOptionLabel(opt)">{{ formatPushCampusOptionLabel(opt) }}</span>
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="匹配项目" prop="matchProjectCodes">
              <el-cascader
                v-model="pushForm.matchProjectCodes"
                :options="pushProjectCascaderOptions"
                :props="{ multiple: true, checkStrictly: true, emitPath: false, expandTrigger: 'hover' }"
                :placeholder="pushProjectDisabled ? '请先选择机构品牌' : '请选择匹配项目（可多选）'"
                style="width: 350px;"
                clearable
                filterable
                collapse-tags
                :disabled="pushProjectDisabled"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="提交方式" prop="submitMethod">
              <el-select v-model="pushForm.submitMethod" placeholder="请选择" style="width: 350px;" clearable>
                <el-option v-for="item in submitMethodOptions" :key="item.code" :label="item.name" :value="item.code" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="状态" prop="orderStatusCode">
              <el-select v-model="pushForm.orderStatusCode" placeholder="请选择" style="width: 350px;" @change="onPushOrderStatusChange">
                <el-option
                  v-for="item in orderStatusOptions"
                  :key="item.code"
                  :label="formatPushOrderStatusOptionLabel(item)"
                  :value="item.code"
                  :disabled="isPushOrderStatusOptionDisabled(item)"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="推送人">
              <el-input v-model="pushForm.pushUserName" disabled style="width: 350px;" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row v-if="showPushDealAmountFields" :gutter="16">
          <el-col :span="12">
            <el-form-item label="成交金额（单位：元）" prop="payAmount" :required="pushDealAmountRequired">
              <el-input-number
                v-model="pushForm.payAmount"
                :min="0"
                :max="999999.99"
                :step="0.01"
                :precision="2"
                controls-position="right"
                style="width: 350px;"
                placeholder="请输入成交金额"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="返佣（单位：元）" prop="commissionAmount" :required="pushDealAmountRequired">
              <el-input-number
                v-model="pushForm.commissionAmount"
                :min="0"
                :max="999999.99"
                :step="0.01"
                :precision="2"
                controls-position="right"
                style="width: 350px;"
                placeholder="请输入返佣金额"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row v-if="showPushRefundFields" :gutter="16">
          <el-col :span="12">
            <el-form-item label="退费金额（单位：元）" prop="refundAmount" :required="pushRefundRequired">
              <el-input-number
                v-model="pushForm.refundAmount"
                :min="0"
                :max="999999.99"
                :step="0.01"
                :precision="2"
                controls-position="right"
                style="width: 350px;"
                placeholder="请输入退费金额"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="退还返佣（单位：元）" prop="refundCommissionAmount" :required="pushRefundRequired">
              <el-input-number
                v-model="pushForm.refundCommissionAmount"
                :min="0"
                :max="999999.99"
                :step="0.01"
                :precision="2"
                controls-position="right"
                style="width: 350px;"
                placeholder="请输入退还返佣"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="备注">
          <el-input
            v-model="pushForm.projectRemark"
            type="textarea"
            :rows="2"
            placeholder="请输入备注（最多500字）"
            maxlength="500"
            show-word-limit
            style="width: 100%;"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="pushFormVisible = false">取消</el-button>
        <el-button :type="showConfirmAndCreateOrderBtn ? '' : 'primary'" @click="submitPushRecord()" :loading="pushSubmitLoading" :disabled="pushSubmitLoading">确定</el-button>
        <el-button
          v-if="showConfirmAndCreateOrderBtn"
          type="primary"
          @click="submitPushRecord({ forceCreateOrder: true })"
          :loading="pushSubmitLoading"
          :disabled="pushSubmitLoading"
        >确定并创建订单</el-button>
      </span>
    </el-dialog>

    <!-- 跟进记录表单弹窗 -->
    <el-dialog title="添加跟进记录" :visible.sync="followFormVisible" width="920px" :close-on-click-modal="false" append-to-body class="follow-form-dialog">
      <el-form :model="followForm" ref="followForm" :rules="followRules" label-width="140px">
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="跟进时间" prop="followTime" required>
              <el-date-picker v-model="followForm.followTime" type="datetime" placeholder="选择时间" style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="是否有效" prop="validStatusCode" required>
              <el-select v-model="followForm.validStatusCode" placeholder="请选择" style="width: 100%;" @change="onFollowValidStatusChange">
                <el-option v-for="item in validStatusOptions" :key="item.code" :label="item.name" :value="item.code" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="下次联系时间" prop="nextContactTime" :required="followForm.validStatusCode !== 'INVALID' && !followForm.stopFollowUp">
              <div class="next-contact-control">
                <el-date-picker
                  v-model="followForm.nextContactTime"
                  type="datetime"
                  placeholder="选择时间"
                  class="next-contact-picker"
                  :disabled="followForm.validStatusCode === 'INVALID' || followForm.stopFollowUp"
                  :default-time="followNextPickerDefaultTime"
                />
                <el-checkbox v-model="followForm.stopFollowUp" class="stop-follow-up-checkbox" @change="onStopFollowUpChange">停止回访</el-checkbox>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="跟进人">
              <el-input v-model="followForm.followUserName" disabled style="width: 100%;" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="跟进内容" prop="followContent" required class="follow-content-full">
          <el-input v-model="followForm.followContent" type="textarea" :rows="4" placeholder="请输入跟进内容（至少10字，最多500字）" maxlength="500" show-word-limit style="width: 100%;" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="followFormVisible = false" :disabled="followSubmitLoading">取消</el-button>
        <el-button type="primary" @click="submitFollowRecord" :loading="followSubmitLoading" :disabled="followSubmitLoading">确定</el-button>
      </span>
    </el-dialog>

    <el-dialog title="自定义列配置" :visible.sync="columnConfigVisible" width="520px" :close-on-click-modal="false">
      <div class="column-config-tip">拖动可调整列顺序；“学员姓名”为默认展示项，不可取消。</div>
      <div class="column-config-list">
        <div
          v-for="(col, index) in columnConfigDraft"
          :key="col.key"
          class="column-config-item"
          draggable="true"
          @dragstart="handleColumnDragStart(index)"
          @dragover.prevent
          @drop="handleColumnDrop(index)"
        >
          <el-checkbox v-model="col.visible" :disabled="col.key === 'studentName'">{{ getColumnLabel(col.key) }}</el-checkbox>
          <span v-if="col.key === 'studentName'" class="column-fixed-tip">默认</span>
          <i class="el-icon-d-caret column-drag-icon"></i>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="resetColumnConfigDraft">恢复默认列</el-button>
        <el-button @click="columnConfigVisible = false">取消</el-button>
        <el-button type="primary" @click="saveColumnConfig">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import api from '../services/api';
import RegionCascader from '@/components/RegionCascader.vue';
import UtwlOrgSelect from '@/components/utwl-org-select.vue';
import { getAccount, getUserRole, getRoleCode, getToken, decodeJwtPayload, canShowOrgStaffStudentListDelete } from '@/utils/sessionAuth';

const DEFAULT_FOLLOW_CONTENT = '电话号码不重复，可以正常跟进！【此内容来自于系统默认】';

/** 学生详情内推送/跟进/修改记录共用的「列表 | 时间线」偏好（按登录账号隔离） */
function detailRecordsViewStorageKey(userKey) {
  return `studentManage:studentDetail:recordsView:${encodeURIComponent(userKey)}`;
}

function formatDateTime(date) {
  const pad = n => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function createDefaultConsultTimeRange() {
  const end = new Date();
  end.setHours(23, 59, 59, 0);
  const start = new Date(end);
  start.setMonth(start.getMonth() - 1);
  start.setHours(0, 0, 0, 0);
  return [formatDateTime(start), formatDateTime(end)];
}

function createDefaultSearchForm() {
  return {
    studentName: '',
    phoneNumber: '',
    sourceCode: '',
    projectCodes: [],
    orgId: '',
    enrolledOrgId: '',
    validStatusCode: 'PENDING',
    referrerStaffId: '',
    consultTimeRange: createDefaultConsultTimeRange()
  };
}

export default {
  name: 'StudentManage',
  components: {
    RegionCascader,
    UtwlOrgSelect
  },
  data() {
    return {
      loading: false,
      submitLoading: false,
      searchLoading: false,
      deleteLoading: false,
      batchDeleteLoading: false,
      exportLoading: false,
      viewMode: 'list',
      tableData: [],
      selectedRows: [],
      total: 0,
      currentPage: 1,
      pageSize: 10,
      countdownTimer: null,
      currentTime: Date.now(),
      listServerNowEpochMs: Date.now(),
      listLoadedClientAt: Date.now(),
      searchForm: createDefaultSearchForm(),
      sortField: 'createTime',
      sortOrder: 'desc',
      sourceOptions: [],
      projectOptions: [],
      projectCascaderOptions: [],
      mainProjectMap: {},
      validStatusOptions: [],
      orderStatusOptions: [
        { code: 'TO_SCHOOL', name: '给学校' },
        { code: 'SCHOOL_PAID', name: '学校收款' },
        { code: 'INVALID', name: '无效订单' },
        { code: 'VERIFIED', name: '已核对' },
        { code: 'SETTLED', name: '已结算' },
        { code: 'ISSUE', name: '问题单' },
        { code: 'DISPUTED', name: '疑义订单' },
        { code: 'RECHECK', name: '再核对' },
        { code: 'REFUND', name: '退费' }
      ],
      submitMethodOptions: [],
      /** 字典 STUDENT_ENROLL_STATUS：已报名 / 未报名 / 退费等 */
      enrollStatusOptions: [],
      orgOptions: [],
      orgOptionsLoading: false,
      orgQueryKeyword: '',
      orgQueryPage: 1,
      orgQueryPageSize: 10,
      orgQueryTotal: 0,
      filterReferrerStaffOptions: [],
      filterReferrerStaffLoading: false,
      filterReferrerStaffKeyword: '',
      filterReferrerStaffPage: 1,
      filterReferrerStaffPageSize: 10,
      filterReferrerStaffTotal: 0,
      dialogVisible: false,
      dialogTitle: '新增学生',
      isEdit: false,
      editId: null,
      form: {
        studentName: '',
        contacts: [{ phone: '', wechat: '' }],
        sourceCode: '',
        projectCodes: [],
        cityCode: '',
        referrerStaffId: '',
        referrerName: '',
        addressDetail: '',
        remark: '',
        validStatusCode: 'PENDING'
      },
      rules: {
        studentName: [{ required: true, message: '请输入学员姓名', trigger: ['blur', 'change'] }],
        contacts: [{ validator: (rule, value, callback) => {
          if (!Array.isArray(value) || value.length === 0) {
            callback(new Error('请至少填写一条手机号'));
            return;
          }
          const firstPhone = (value[0].phone || '').trim();
          if (!firstPhone) {
            callback(new Error('请至少填写一条手机号'));
            return;
          }
          const hasWechatWithoutPhone = value.some(item => (item.wechat || '').trim() && !(item.phone || '').trim());
          if (hasWechatWithoutPhone) {
            callback(new Error('微信号所在行需先填写手机号'));
            return;
          }
          const phoneReg = /^1[3-9]\d{9}$/;
          const wechatReg = /^[a-zA-Z][-_a-zA-Z0-9]{5,19}$/;
          const invalidPhone = value.find(item => {
            const p = (item.phone || '').trim();
            return p && !phoneReg.test(p.replace(/\s+/g, '')) && !wechatReg.test(p);
          });
          if (invalidPhone) {
            callback(new Error('请输入正确的电话或者微信号'));
            return;
          }
          callback();
        }, trigger: ['blur', 'change'] }],
        sourceCode: [{ required: true, message: '请选择来源渠道', trigger: ['blur', 'change'] }],
        projectCodes: [{ required: true, message: '请选择咨询课程', trigger: ['blur', 'change'] }],
        cityCode: [{ required: true, message: '请选择所在城市', trigger: ['blur', 'change'] }],
        addressDetail: [{ required: true, message: '请输入详细地址', trigger: ['blur', 'change'] }]
      },
      referrerStaffOptions: [],
      referrerStaffLoading: false,
      detailVisible: false,
      detailData: null,
      detailNeedRefreshList: false,
      activeTab: 'push',
      /** 学生详情内推送/跟进/修改记录共用：列表 | 时间线（默认列表，读写 localStorage） */
      detailRecordsDisplayMode: 'list',
      /** 推送 / 跟进时间线 el-descriptions 标签列样式（与原 apple-timeline-kv .k 一致） */
      pushTimelineDescLabelStyle: {
        minWidth: '72px',
        color: 'rgba(60, 60, 67, 0.55)',
        fontWeight: '500'
      },
      pushRecords: [],
      followRecords: [],
      /** 学员基础信息修改记录（仅时间线，按 updated_at） */
      basicChangeLogs: [],
      pushFormVisible: false,
      /** 推送记录弹窗：add | edit */
      pushFormMode: 'add',
      editingPushRecordId: null,
      editingPushOriginalOrderStatusCode: '',
      editingPushOrderNo: '',
      originalPushEnrollStatus: '',
      pushForm: {
        orgId: '',
        orgCampus: '',
        matchProjectCodes: [],
        projectRemark: '',
        submitMethod: '',
        pushUserName: '',
        orderStatusCode: 'TO_SCHOOL',
        enrollStatus: 'NOT_ENROLLED',
        payAmount: null,
        commissionAmount: null,
        refundAmount: null,
        refundCommissionAmount: null
      },
      pushRules: {
        orgId: [{ required: true, message: '请选择机构品牌', trigger: ['blur', 'change'] }],
        orgCampus: [{ required: true, message: '请选择机构校区', trigger: ['blur', 'change'] }],
        matchProjectCodes: [{ required: true, message: '请选择匹配项目', trigger: ['blur', 'change'], type: 'array', min: 1 }],
        submitMethod: [{ required: true, message: '请选择提交方式', trigger: ['blur', 'change'] }],
        orderStatusCode: [{ required: true, message: '请选择状态', trigger: ['blur', 'change'] }],
        payAmount: [{ validator: (rule, value, callback) => {
          if (!this.pushDealAmountRequired) return callback();
          if (value === '' || value === null || value === undefined) return callback(new Error('请输入成交金额'));
          if (typeof value !== 'number' || Number.isNaN(value)) return callback(new Error('成交金额必须为数字'));
          if (!/^\d+(\.\d{1,2})?$/.test(String(value))) return callback(new Error('成交金额最多保留2位小数'));
          if (Number(value) < 0) return callback(new Error('成交金额不能小于0'));
          const commission = Number(this.pushForm.commissionAmount);
          if (Number.isFinite(commission) && Number(value) < commission) {
            return callback(new Error('成交金额必须大于等于返佣'));
          }
          callback();
        }, trigger: ['blur', 'change'] }],
        commissionAmount: [{ validator: (rule, value, callback) => {
          if (!this.pushDealAmountRequired) return callback();
          if (value === '' || value === null || value === undefined) return callback(new Error('请输入返佣金额'));
          if (typeof value !== 'number' || Number.isNaN(value)) return callback(new Error('返佣必须为数字'));
          if (!/^\d+(\.\d{1,2})?$/.test(String(value))) return callback(new Error('返佣最多保留2位小数'));
          if (Number(value) < 0) return callback(new Error('返佣金额不能小于0'));
          const pay = Number(this.pushForm.payAmount);
          if (Number.isFinite(pay) && pay < Number(value)) {
            return callback(new Error('返佣不能大于成交金额'));
          }
          callback();
        }, trigger: ['blur', 'change'] }],
        refundAmount: [{ validator: (rule, value, callback) => {
          if (this.pushForm.enrollStatus !== 'REFUND') return callback();
          if (value === '' || value === null || value === undefined) return callback(new Error('请输入退费金额'));
          if (typeof value !== 'number' || Number.isNaN(value)) return callback(new Error('退费金额必须为数字'));
          if (!/^\d+(\.\d{1,2})?$/.test(String(value))) return callback(new Error('退费金额最多保留2位小数'));
          if (Number(value) < 0) return callback(new Error('退费金额不能小于0'));
          callback();
        }, trigger: ['blur', 'change'] }],
        refundCommissionAmount: [{ validator: (rule, value, callback) => {
          if (this.pushForm.enrollStatus !== 'REFUND') return callback();
          if (value === '' || value === null || value === undefined) return callback(new Error('请输入退还返佣'));
          if (typeof value !== 'number' || Number.isNaN(value)) return callback(new Error('退还返佣必须为数字'));
          if (!/^\d+(\.\d{1,2})?$/.test(String(value))) return callback(new Error('退还返佣最多保留2位小数'));
          if (Number(value) < 0) return callback(new Error('退还返佣不能小于0'));
          callback();
        }, trigger: ['blur', 'change'] }]
      },
      pushSubmitLoading: false,
      followFormVisible: false,
      /** 下次联系时间 datetime 选中日历日期后沿用的默认时刻（与打开弹窗时客户端当前时分秒一致） */
      followNextPickerDefaultTime: '00:00:00',
      followForm: {
        followTime: new Date(),
        nextContactTime: null,
        followUserName: '',
        followContent: DEFAULT_FOLLOW_CONTENT,
        validStatusCode: 'PENDING',
        stopFollowUp: false
      },
      followRules: {
        followTime: [{ validator: (rule, value, callback) => {
          if (value === null || value === undefined || value === '') {
            callback(new Error('请选择跟进时间'));
          } else {
            callback();
          }
        }, trigger: ['blur', 'change'] }],
        validStatusCode: [{ validator: (rule, value, callback) => {
          if (!value) {
            callback(new Error('请选择是否有效'));
          } else {
            callback();
          }
        }, trigger: ['blur', 'change'] }],
        nextContactTime: [{ validator: (rule, value, callback) => {
          if (this.followForm.validStatusCode === 'INVALID' || this.followForm.stopFollowUp) {
            callback();
            return;
          }
          if (value === null || value === undefined || value === '') {
            callback(new Error('请选择下次联系时间'));
          } else {
            callback();
          }
        }, trigger: ['blur', 'change'] }],
        followContent: [
          { validator: (rule, value, callback) => {
            if (!value || (typeof value === 'string' && !value.trim())) {
              callback(new Error('请输入跟进内容'));
            } else if (String(value).length < 10) {
              callback(new Error('跟进内容至少10个字'));
            } else {
              callback();
            }
          }, trigger: ['blur', 'change'] }
        ]
      },
      followSubmitLoading: false,
      pushCampusOptionsMap: {},
      pushOrgMainProjectCodesMap: {},
      columnConfigVisible: false,
      draggingColumnIndex: -1,
      allTableColumns: [
        { key: 'studentName', label: '学员姓名', minWidth: 150 },
        { key: 'phoneNumber', label: '手机号', prop: 'phoneNumber', width: 100,align:"center" },
        { key: 'sourceName', label: '来源渠道', prop: 'sourceName', width: 75 },
        { key: 'consultCourse', label: '咨询课程', minWidth: 150 },
        { key: 'orgName', label: '推荐机构', prop: 'orgName', minWidth: 100 },
        { key: 'campusName', label: '推荐校区', prop: 'campusName', minWidth: 150 },
        { key: 'enrolledOrgName', label: '报名机构', prop: 'enrolledOrgName', minWidth: 100 },
        { key: 'courseName', label: '课程名', prop: 'courseName', minWidth: 120 },
        { key: 'followPayTime', label: '回访收款时间', prop: 'followPayTime', minWidth: 160 },
        { key: 'verifyTime', label: '核对时间', prop: 'verifyTime', minWidth: 160 },
        { key: 'followerName', label: '跟进人', prop: 'followerName', minWidth: 100 },
        { key: 'cityDisplay', label: '所在城市', minWidth: 130 },
        { key: 'validStatus', label: '是否有效', minWidth: 75 },
        { key: 'referrerName', label: '推荐人', prop: 'referrerName', minWidth: 100 },
        { key: 'countdown', label: '回访倒计时', prop: 'countdown', minWidth: 130 },
        { key: 'remark', label: '备注', prop: 'remark', minWidth: 100 },
        { key: 'createTime', label: '咨询时间', prop: 'createTime', minWidth: 100 }
      ],
      defaultColumnConfigList: [
        { key: 'studentName', visible: true },
        { key: 'phoneNumber', visible: true },
        { key: 'sourceName', visible: true },
        { key: 'consultCourse', visible: true },
        { key: 'createTime', visible: true },
        { key: 'validStatus', visible: true },
        { key: 'referrerName', visible: false },
        { key: 'countdown', visible: true },
        { key: 'orgName', visible: true },
        { key: 'campusName', visible: true },
        { key: 'enrolledOrgName', visible: true },
        { key: 'courseName', visible: false },
        { key: 'followPayTime', visible: false },
        { key: 'verifyTime', visible: false },
        { key: 'followerName', visible: false },
        { key: 'cityDisplay', visible: false },
        { key: 'remark', visible: false }
      ],
      columnConfigList: [
        { key: 'studentName', visible: true },
        { key: 'phoneNumber', visible: true },
        { key: 'sourceName', visible: true },
        { key: 'consultCourse', visible: true },
        { key: 'createTime', visible: true },
        { key: 'validStatus', visible: true },
        { key: 'referrerName', visible: false },
        { key: 'countdown', visible: true },
        { key: 'orgName', visible: true },
        { key: 'campusName', visible: true },
        { key: 'enrolledOrgName', visible: true },
        { key: 'courseName', visible: false },
        { key: 'followPayTime', visible: false },
        { key: 'verifyTime', visible: false },
        { key: 'followerName', visible: false },
        { key: 'cityDisplay', visible: false },
        { key: 'remark', visible: false }
      ],
      columnConfigDraft: [],
      studentTableHeight: 420
    };
  },
  computed: {
    /** 核算人员（FINANCE）不可新增学生，且详情内不可添加跟进/推送记录 */
    canShowStudentWriteActions() {
      const rc = (getRoleCode() || '').trim();
      const ur = (getUserRole() || '').trim();
      return rc !== 'FINANCE' && ur !== 'FINANCE';
    },
    canShowManageListDelete() {
      return canShowOrgStaffStudentListDelete();
    },
    isOperationRole() {
      const rc = (getRoleCode() || '').trim();
      const ur = (getUserRole() || '').trim();
      return rc === 'OPERATION' || ur === 'OPERATION';
    },
    isFinanceRole() {
      const rc = (getRoleCode() || '').trim();
      const ur = (getUserRole() || '').trim();
      return rc === 'FINANCE' || ur === 'FINANCE';
    },
    isAdmin() {
      const account = getAccount() || '';
      return account.toLowerCase() === 'admin';
    },
    /** 平台用户且角色为 admin / SYSTEM_ADMIN，或历史账号 admin（本地兼容） */
    isPrivilegedStudentEditor() {
      const role = (getUserRole() || '').trim();
      if (role === 'admin' || role === 'SYSTEM_ADMIN') return true;
      return this.isAdmin;
    },
    /** 详情中：最新推送记录报名状态为退费时冻结（管理员不受限） */
    isDetailRefundFrozen() {
      if (this.isPrivilegedStudentEditor) return false;
      const latest = (this.pushRecords && this.pushRecords.length) ? this.pushRecords[0] : null;
      const code = latest
        ? this.resolvePushRecordEnrollCode(latest)
        : (this.detailData && this.detailData.enrollStatusCode ? this.detailData.enrollStatusCode : null);
      return code === 'REFUND';
    },
    activeTableColumns() {
      const columnMap = this.allTableColumns.reduce((acc, item) => {
        acc[item.key] = item;
        return acc;
      }, {});
      return this.columnConfigList
        .filter(item => item.visible)
        .map(item => columnMap[item.key])
        .filter(Boolean);
    },
    pushCampusOptions() {
      const selectedOrgId = this.pushForm && this.pushForm.orgId;
      if (!selectedOrgId) return [];
      let raw = this.pushCampusOptionsMap[String(selectedOrgId)];
      if (!Array.isArray(raw) || !raw.length) {
        const selectedOrg = (this.orgOptions || []).find(item => String(item.orgId) === String(selectedOrgId));
        raw = selectedOrg && Array.isArray(selectedOrg.campusOptions) ? selectedOrg.campusOptions : [];
      }
      return this.normalizePushCampusRows(raw);
    },
    pushProjectDisabled() {
      return !(this.pushForm && this.pushForm.orgId);
    },
    /** 匹配项目仅随「机构品牌」联动：候选项 = 该机构主营项目 mainProjectCodes 与字典 MAIN_PROJECT 交集（支持机构只配父级 code） */
    pushProjectCascaderOptions() {
      if (this.pushProjectDisabled) return [];
      const selectedOrgId = String((this.pushForm && this.pushForm.orgId) || '').trim();
      if (!selectedOrgId) return [];
      let rawCodes =
        this.pushOrgMainProjectCodesMap[selectedOrgId] != null
          ? this.pushOrgMainProjectCodesMap[selectedOrgId]
          : null;
      if (rawCodes == null) {
        const selectedOrg = (this.orgOptions || []).find(item => String(item.orgId) === selectedOrgId);
        rawCodes = selectedOrg && selectedOrg.mainProjectCodes != null ? selectedOrg.mainProjectCodes : [];
      }
      const enabledCodes = this.normalizeCodeList(rawCodes);
      if (!enabledCodes.length) return [];
      const enabledSet = new Set(enabledCodes);
      (this.projectCascaderOptions || []).forEach((parent) => {
        const pv = String(parent.value || '').trim();
        if (!pv || !enabledSet.has(pv)) return;
        (parent.children || []).forEach((child) => {
          const cv = String(child.value || '').trim();
          if (cv) enabledSet.add(cv);
        });
      });
      return (this.projectCascaderOptions || [])
        .map(parent => ({
          ...parent,
          children: Array.isArray(parent.children)
            ? parent.children.filter(child => enabledSet.has(String(child.value || '').trim()))
            : []
        }))
        .filter(parent => Array.isArray(parent.children) && parent.children.length > 0);
    },
    basicInfoTitle() {
      return '基础信息';
    },
    orgQueryTotalPages() {
      const total = Number(this.orgQueryTotal || 0);
      const size = Number(this.orgQueryPageSize || 10);
      return Math.max(1, Math.ceil(total / size));
    },
    filterReferrerStaffTotalPages() {
      const total = Number(this.filterReferrerStaffTotal || 0);
      const size = Number(this.filterReferrerStaffPageSize || 10);
      return Math.max(1, Math.ceil(total / size));
    },
    tableColumnRenderKey() {
      return this.columnConfigList.map(item => `${item.key}:${item.visible ? 1 : 0}`).join('|');
    },
    /** 详情中学生为「无效」且非管理员时，禁止再添加跟进/推送 */
    isDetailStudentInvalid() {
      return !!(this.detailData && this.detailData.validStatusCode === 'INVALID' && !this.isPrivilegedStudentEditor);
    },
    showPushDealAmountFields() {
      const isEnrolled = this.pushForm && this.pushForm.enrollStatus === 'ENROLLED';
      return isEnrolled || this.isFinanceRole;
    },
    pushDealAmountRequired() {
      return this.showPushDealAmountFields;
    },
    showPushRefundFields() {
      return (this.pushForm && this.pushForm.enrollStatus === 'REFUND') || this.isFinanceRole;
    },
    pushRefundRequired() {
      return this.pushForm && this.pushForm.enrollStatus === 'REFUND';
    },
    showConfirmAndCreateOrderBtn() {
      return !!(
        this.pushFormMode === 'edit'
        && this.isFinanceRole
        && this.pushForm
        && String(this.pushForm.orderStatusCode || '').trim() === 'SETTLED'
      );
    },
    isFinanceEditOrderCreatedRestricted() {
      const originalStatus = String(this.editingPushOriginalOrderStatusCode || '').trim();
      const orderNo = String(this.editingPushOrderNo || '').trim();
      return !!(
        this.pushFormMode === 'edit'
        && this.isFinanceRole
        && originalStatus === 'SETTLED'
        && !!orderNo
      );
    }
  },
  mounted() {
    this.loadColumnConfig();
    this.loadDictionary();
    this.loadData();
    this.startCountdownTimer();
    this.loadDetailRecordsViewPreferences();
    this.$nextTick(() => {
      this.updateStudentTableHeight();
    });
    window.addEventListener('resize', this.updateStudentTableHeight);
  },
  beforeDestroy() {
    this.stopCountdownTimer();
    window.removeEventListener('resize', this.updateStudentTableHeight);
  },
  methods: {
    updateStudentTableHeight() {
      if (this.viewMode !== 'list') return;
      const tableCardRef = this.$refs.tableCard;
      const tableCardEl = tableCardRef && tableCardRef.$el ? tableCardRef.$el : null;
      if (!tableCardEl) return;

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 900;
      const tableTop = tableCardEl.getBoundingClientRect().top;
      const paginationEl = this.$refs.paginationWrapper;
      const paginationHeight = paginationEl ? paginationEl.getBoundingClientRect().height : 56;
      const safeBottomGap = 12;
      const computed = Math.floor(viewportHeight - tableTop - paginationHeight - safeBottomGap);
      this.studentTableHeight = Math.max(260, computed);

      this.$nextTick(() => {
        const tableRef = this.$refs.studentListTable;
        if (tableRef && typeof tableRef.doLayout === 'function') {
          tableRef.doLayout();
        }
      });
    },
    indexMethod(index) {
      return (this.currentPage - 1) * this.pageSize + index + 1;
    },
    studentRowSelectable() {
      return this.canShowManageListDelete;
    },
    removeDuplicates(arr) {
      const seen = new Set();
      return arr.filter(item => {
        if (seen.has(item.code)) return false;
        seen.add(item.code);
        return true;
      });
    },
    isMobilePhone(raw) {
      const v = String(raw || '').trim().replace(/\s+/g, '');
      return /^1[3-9]\d{9}$/.test(v);
    },
    isWechatId(raw) {
      const v = String(raw || '').trim();
      return /^[a-zA-Z][-_a-zA-Z0-9]{5,19}$/.test(v);
    },
    isPhoneOrWechatId(raw) {
      return this.isMobilePhone(raw) || this.isWechatId(raw);
    },
    async onContactPhoneBlur(rawPhone) {
      const raw = String(rawPhone || '').trim();
      if (!raw) return;
      if (!this.isPhoneOrWechatId(raw)) {
        this.$message.warning('请输入正确的电话或者微信号');
        return;
      }
      const value = raw.replace(/\s+/g, '');
      try {
        const res = await api.post('/student/phone-duplicate-check', {
          phoneNumber: value,
          studentId: this.isEdit ? this.editId : ''
        });
        const duplicated = !!(res && res.data && res.data.data && res.data.data.duplicated);
        if (!duplicated) return;
        await this.$alert(
          this.isMobilePhone(value)
            ? '您当前录入的电话已经入库，请核实！'
            : '您当前录入的微信号已经入库，请核实！',
          '温馨提示！',
          {
            confirmButtonText: '我知道了',
            type: 'warning'
          }
        );
      } catch (e) {
        // 查重失败不阻塞新增/编辑流程
      }
    },
    getCurrentUserKey() {
      return getAccount() || 'anonymous';
    },
    /**
     * 获取当前登录用户显示名（优先 token.nickname；否则 fallback 到 localStorage.account）
     * 用于推送记录弹窗“推送人”回填，避免误用旧的 localStorage.account
     */
    getCurrentUserDisplayName() {
      const fallback = getAccount() || '管理员';
      try {
        const token = getToken();
        if (!token) return fallback;
        const payload = decodeJwtPayload(token);
        if (!payload) return fallback;
        return payload.nickname || payload.phone || fallback;
      } catch (e) {
        return fallback;
      }
    },
    readStoredDetailRecordsViewMode() {
      try {
        const uk = this.getCurrentUserKey();
        const key = detailRecordsViewStorageKey(uk);
        let v = localStorage.getItem(key);
        if (v === 'list' || v === 'timeline') return v;
        for (const tab of ['push', 'follow', 'history']) {
          const legacyKey = `studentManage:studentDetail:recordsView:${tab}:${encodeURIComponent(uk)}`;
          v = localStorage.getItem(legacyKey);
          if (v === 'list' || v === 'timeline') {
            localStorage.setItem(key, v);
            return v;
          }
        }
      } catch (e) {
        /* ignore */
      }
      return 'list';
    },
    persistDetailRecordsDisplayMode() {
      const mode = this.detailRecordsDisplayMode;
      if (mode !== 'list' && mode !== 'timeline') return;
      try {
        localStorage.setItem(detailRecordsViewStorageKey(this.getCurrentUserKey()), mode);
      } catch (e) {
        /* ignore */
      }
    },
    loadDetailRecordsViewPreferences() {
      this.detailRecordsDisplayMode = this.readStoredDetailRecordsViewMode();
    },
    normalizeColumnConfig(configList) {
      const allKeys = this.allTableColumns.map(item => item.key);
      const defaultMap = this.defaultColumnConfigList.reduce((acc, item) => {
        acc[item.key] = !!item.visible;
        return acc;
      }, {});
      const orderedKeys = this.defaultColumnConfigList.map(item => item.key).filter(key => allKeys.includes(key));
      const seen = new Set();
      const merged = [];
      const hasInput = Array.isArray(configList) && configList.length > 0;
      const shouldApplyNewDefaultColumns = hasInput && !(configList || []).some(item => item && item.key === 'enrolledOrgName');
      const newDefaultColumnKeys = new Set(['referrerName', 'orgName', 'campusName']);
      (Array.isArray(configList) ? configList : []).forEach(item => {
        if (!item || !allKeys.includes(item.key) || seen.has(item.key)) return;
        seen.add(item.key);
        const visible = shouldApplyNewDefaultColumns && newDefaultColumnKeys.has(item.key)
          ? !!defaultMap[item.key]
          : (typeof item.visible === 'boolean' ? item.visible : !!defaultMap[item.key]);
        merged.push({
          key: item.key,
          visible: item.key === 'studentName' ? true : visible
        });
      });

      const appendMissingByDefaultOrder = () => {
        orderedKeys.forEach(key => {
          if (!seen.has(key)) {
            merged.push({ key, visible: key === 'studentName' ? true : !!defaultMap[key] });
            seen.add(key);
          }
        });
        allKeys.forEach(key => {
          if (!seen.has(key)) {
            merged.push({ key, visible: key === 'studentName' ? true : !!defaultMap[key] });
            seen.add(key);
          }
        });
      };

      if (!hasInput) {
        merged.length = 0;
        seen.clear();
        appendMissingByDefaultOrder();
      } else {
        appendMissingByDefaultOrder();
      }

      if (!merged.some(item => item.key === 'studentName')) {
        merged.unshift({ key: 'studentName', visible: true });
      }
      return merged.map(item => ({ ...item, visible: item.key === 'studentName' ? true : item.visible }));
    },
    getColumnLabel(key) {
      const target = this.allTableColumns.find(item => item.key === key);
      return target ? target.label : key;
    },
    async loadColumnConfig() {
      try {
        const res = await api.post('/student/list-columns/query', { userKey: this.getCurrentUserKey() });
        if (res.data && res.data.success) {
          this.columnConfigList = this.normalizeColumnConfig(res.data.data || []);
        } else {
          this.columnConfigList = this.normalizeColumnConfig(this.columnConfigList);
        }
      } catch (e) {
        this.columnConfigList = this.normalizeColumnConfig(this.columnConfigList);
      }
    },
    openColumnConfigDialog() {
      this.columnConfigDraft = this.columnConfigList.map(item => ({ ...item }));
      this.columnConfigVisible = true;
    },
    resetColumnConfigDraft() {
      this.columnConfigDraft = this.defaultColumnConfigList.map(item => ({ ...item }));
    },
    handleColumnDragStart(index) {
      this.draggingColumnIndex = index;
    },
    handleColumnDrop(index) {
      const fromIndex = this.draggingColumnIndex;
      this.draggingColumnIndex = -1;
      if (fromIndex < 0 || fromIndex === index) return;
      const moved = this.columnConfigDraft.splice(fromIndex, 1)[0];
      this.columnConfigDraft.splice(index, 0, moved);
    },
    async saveColumnConfig() {
      const normalized = this.normalizeColumnConfig(this.columnConfigDraft);
      try {
        const res = await api.post('/student/list-columns', {
          userKey: this.getCurrentUserKey(),
          columns: normalized
        });
        if (!res.data.success) {
          this.$message.error(res.data.message || '保存失败');
          return;
        }
        this.columnConfigList = normalized;
        this.columnConfigVisible = false;
        this.$message.success('自定义列已保存');
      } catch (e) {
        this.$message.error('保存失败');
      }
    },
    async loadDictionary() {
      try {
        const res = await api.post('/dictionary', { tags: ['STUDENT_SOURCE', 'STUDENT_PROJECT', 'STUDENT_VALID_STATUS', 'PUSH_SUBMIT_METHOD', 'MAIN_PROJECT', 'STUDENT_ENROLL_STATUS'] });
        if (res.data.success) {
          const dict = res.data.data || {};
          this.sourceOptions = this.removeDuplicates(dict.STUDENT_SOURCE || []);
          this.projectOptions = this.removeDuplicates(dict.STUDENT_PROJECT || []);
          this.validStatusOptions = dict.STUDENT_VALID_STATUS || [];
          this.submitMethodOptions = dict.PUSH_SUBMIT_METHOD || [];
          this.enrollStatusOptions = dict.STUDENT_ENROLL_STATUS || [];
          const mainList = dict.MAIN_PROJECT || [];
          this.projectCascaderOptions = this.buildProjectCascader(mainList);
          this.mainProjectMap = {};
          mainList.forEach(item => {
            this.mainProjectMap[item.code] = item.name;
          });
        }
      } catch (e) {
        console.error('加载字典失败', e);
      }
    },
    buildProjectCascader(items) {
      const parentItems = items.filter(item => !item.parent_code);
      return parentItems.map(parent => ({
        value: parent.code,
        label: parent.name,
        children: items
          .filter(child => child.parent_code === parent.code)
          .map(child => ({
            value: child.code,
            label: child.name
          }))
      }));
    },
    normalizeCodeList(rawCodes) {
      if (rawCodes == null || rawCodes === '') return [];
      if (Array.isArray(rawCodes)) {
        return rawCodes.map(x => String(x || '').trim()).filter(Boolean);
      }
      if (typeof rawCodes === 'string') {
        const s = rawCodes.trim();
        if (!s) return [];
        try {
          const parsed = JSON.parse(s);
          if (Array.isArray(parsed)) {
            return parsed.map(x => String(x || '').trim()).filter(Boolean);
          }
        } catch (e) {
          // ignore and fallback to split
        }
        return s.split(/[、,，]/).map(x => x.trim()).filter(Boolean);
      }
      return [];
    },
    syncPushMatchProjectCodes() {
      const allowed = new Set();
      (this.pushProjectCascaderOptions || []).forEach(parent => {
        const parentCode = String(parent.value || '').trim();
        if (parentCode) allowed.add(parentCode);
        (parent.children || []).forEach(child => {
          const code = String(child.value || '').trim();
          if (code) allowed.add(code);
        });
      });
      const curr = Array.isArray(this.pushForm && this.pushForm.matchProjectCodes)
        ? this.pushForm.matchProjectCodes.map(x => String(x || '').trim()).filter(Boolean)
        : [];
      const next = curr.filter(code => allowed.has(code));
      if (next.length !== curr.length) {
        this.$set(this.pushForm, 'matchProjectCodes', next);
      }
    },
    /** 推送记录弹窗：机构选项展示「品牌（状态）」 */
    formatPushOrgOptionLabel(org) {
      if (!org) return '';
      const name = org.orgName != null ? String(org.orgName).trim() : '';
      const st = (org.statusName != null && String(org.statusName).trim())
        ? String(org.statusName).trim()
        : (org.statusCode != null && String(org.statusCode).trim() ? String(org.statusCode).trim() : '');
      if (!name && !st) return '';
      if (!st) return name;
      if (!name) return `（${st}）`;
      return `${name}（${st}）`;
    },
    /** 已暂停机构不可选 */
    isPushOrgOptionDisabled(org) {
      return !!(org && org.statusCode === 'PAUSED');
    },
    /** 从机构 org_campuses JSON 解析校区行（含 statusCode），无数据时用 campusNames 兜底为启用 */
    buildCampusRowsFromOrganCampuses(organCampuses, campusNamesFallback) {
      let parsed = organCampuses;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          parsed = null;
        }
      }
      const byName = new Map();
      if (parsed && typeof parsed === 'object') {
        Object.keys(parsed).forEach(cityKey => {
          const campuses = parsed[cityKey];
          if (!Array.isArray(campuses)) return;
          campuses.forEach(campus => {
            const name = campus && campus.campusName ? String(campus.campusName).trim() : '';
            if (!name) return;
            const sc = campus && String(campus.statusCode || '').trim().toUpperCase() === 'DISABLED' ? 'DISABLED' : 'ENABLED';
            const row = { campusName: name, statusCode: sc };
            if (campus && campus.mainProjectCodes != null) {
              row.mainProjectCodes = campus.mainProjectCodes;
            }
            byName.set(name, row);
          });
        });
      }
      if (!byName.size && campusNamesFallback) {
        String(campusNamesFallback)
          .split('、')
          .map(s => s.trim())
          .filter(Boolean)
          .forEach(name => {
            if (!byName.has(name)) byName.set(name, { campusName: name, statusCode: 'ENABLED' });
          });
      }
      return Array.from(byName.values());
    },
    /** 兼容旧数据：字符串列表或对象列表统一为 { campusName, statusCode } */
    normalizePushCampusRows(list) {
      if (!Array.isArray(list)) return [];
      return list
        .map((item) => {
          if (typeof item === 'string') {
            const campusName = item.trim();
            if (!campusName) return null;
            return { campusName, statusCode: 'ENABLED' };
          }
          const campusName = item.campusName != null ? String(item.campusName).trim() : '';
          if (!campusName) return null;
          const sc = String(item.statusCode || '').trim().toUpperCase() === 'DISABLED' ? 'DISABLED' : 'ENABLED';
          const row = { campusName, statusCode: sc };
          if (item.mainProjectCodes != null) {
            row.mainProjectCodes = item.mainProjectCodes;
          }
          return row;
        })
        .filter(Boolean);
    },
    /** 推送记录弹窗：校区选项「名称（启用|禁用）」 */
    formatPushCampusOptionLabel(row) {
      if (!row || !row.campusName) return '';
      const st = row.statusCode === 'DISABLED' ? '禁用' : '启用';
      return `${row.campusName}（${st}）`;
    },
    isPushCampusOptionDisabled(row) {
      return !!(row && row.statusCode === 'DISABLED');
    },
    normalizeOrgOptions(list) {
      return (Array.isArray(list) ? list : []).map(org => {
        let organCampuses = org.organCampuses;
        if (typeof organCampuses === 'string') {
          try {
            organCampuses = JSON.parse(organCampuses);
          } catch (e) {
            organCampuses = null;
          }
        }
        const campusOptions = this.buildCampusRowsFromOrganCampuses(organCampuses, org.campusNames);
        return {
          ...org,
          campusOptions
        };
      });
    },
    async loadOrgOptions(page = 1, keyword = this.orgQueryKeyword) {
      this.orgOptionsLoading = true;
      try {
        const nextPage = Math.max(1, Number(page || 1));
        const nextKeyword = String(keyword || '').trim();
        const res = await api.post('/org/list', {
          page: nextPage,
          pageSize: this.orgQueryPageSize,
          ...(nextKeyword ? { keyword: nextKeyword } : {})
        });
        if (res.data.success) {
          const data = res.data.data || {};
          this.orgQueryPage = Number(data.page || nextPage) || nextPage;
          this.orgQueryTotal = Number(data.total || 0) || 0;
          this.orgQueryKeyword = nextKeyword;
          this.orgOptions = this.normalizeOrgOptions(data.list || []);
        }
      } catch (e) {
        console.error('加载机构失败', e);
      } finally {
        this.orgOptionsLoading = false;
      }
    },
    formatReferrerStaffOptionLabel(staff) {
      if (!staff) return '';
      const name = staff.staffName || staff.username || staff.staffId || '';
      const phone = staff.phoneNumber || '';
      return phone ? `${name}（${phone}）` : name;
    },
    maskPhoneForTable(phone) {
      const text = String(phone || '').trim();
      if (!text) return '-';
      if (/^1[3-9]\d{9}$/.test(text)) {
        return text.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2');
      }
      return text;
    },
    async loadSearchReferrerStaffOptions(page = 1, keyword = this.filterReferrerStaffKeyword) {
      this.filterReferrerStaffLoading = true;
      try {
        const nextPage = Math.max(1, Number(page || 1));
        const nextKeyword = String(keyword || '').trim();
        const res = await api.post('/staff/list', {
          page: nextPage,
          pageSize: this.filterReferrerStaffPageSize,
          ...(nextKeyword ? { keyword: nextKeyword } : {})
        });
        if (res.data && res.data.success) {
          const data = res.data.data || {};
          this.filterReferrerStaffOptions = data.list || [];
          this.filterReferrerStaffPage = Number(data.page || nextPage) || nextPage;
          this.filterReferrerStaffTotal = Number(data.total || 0) || 0;
          this.filterReferrerStaffKeyword = nextKeyword;
        }
      } catch (e) {
        console.error('加载推荐人筛选列表失败', e);
      } finally {
        this.filterReferrerStaffLoading = false;
      }
    },
    onPushOrgSearch(query) {
      this.orgQueryKeyword = String(query || '').trim();
      this.loadOrgOptions(1, this.orgQueryKeyword);
    },
    changeOrgPage(page) {
      const p = Math.max(1, Number(page || 1));
      if (p === this.orgQueryPage) return;
      this.loadOrgOptions(p, this.orgQueryKeyword);
    },
    onPushOrgDropdownVisibleChange(visible) {
      if (!visible) return;
      if (!Array.isArray(this.orgOptions) || this.orgOptions.length === 0) {
        this.loadOrgOptions(1, this.orgQueryKeyword);
      }
    },
    onSearchReferrerStaff(query) {
      this.filterReferrerStaffKeyword = String(query || '').trim();
      this.loadSearchReferrerStaffOptions(1, this.filterReferrerStaffKeyword);
    },
    changeSearchReferrerPage(page) {
      const p = Math.max(1, Number(page || 1));
      if (p === this.filterReferrerStaffPage) return;
      this.loadSearchReferrerStaffOptions(p, this.filterReferrerStaffKeyword);
    },
    onSearchReferrerDropdownVisibleChange(visible) {
      if (!visible) return;
      if (!Array.isArray(this.filterReferrerStaffOptions) || this.filterReferrerStaffOptions.length === 0) {
        this.loadSearchReferrerStaffOptions(1, this.filterReferrerStaffKeyword);
      }
    },
    onPushFormDialogClosed() {
      this.pushFormMode = 'add';
      this.editingPushRecordId = null;
      this.editingPushOriginalOrderStatusCode = '';
      this.editingPushOrderNo = '';
      this.originalPushEnrollStatus = '';
    },
    normalizeProjectPathText(text) {
      return String(text || '')
        .trim()
        .replace(/\s*[—－-]\s*/g, '-')
        .replace(/\s+/g, '');
    },
    /**
     * 将已保存的「匹配项目」展示文案还原为多选 code。
     * 兼容小程序保存的「父级-子级」路径，同时保留父子节点独立选择。
     */
    matchProjectTextToCodes(text) {
      if (!text || !String(text).trim()) return [];
      const parts = String(text)
        .split(/\s*[、,，]\s*/)
        .map(s => s.trim())
        .filter(Boolean);
      const pathToCode = {};
      const nameToCode = {};
      (this.projectCascaderOptions || []).forEach(parent => {
        const parentCode = String(parent.value || '').trim();
        const parentName = String(parent.label || '').trim();
        if (parentCode && parentName && nameToCode[parentName] === undefined) {
          nameToCode[parentName] = parentCode;
        }
        (parent.children || []).forEach(child => {
          const childCode = String(child.value || '').trim();
          const childName = String(child.label || '').trim();
          if (childCode && childName && nameToCode[childName] === undefined) {
            nameToCode[childName] = childCode;
          }
          const pathKey = this.normalizeProjectPathText(`${parentName}-${childName}`);
          if (childCode && pathKey) {
            pathToCode[pathKey] = childCode;
          }
        });
      });
      Object.keys(this.mainProjectMap || {}).forEach(code => {
        const name = this.mainProjectMap[code];
        if (name && nameToCode[name] === undefined) nameToCode[name] = code;
      });
      const seen = new Set();
      return parts.map(p => {
        const raw = String(p || '').trim();
        const pathKey = this.normalizeProjectPathText(raw);
        const lastName = raw.split(/\s*[—－-]\s*/).map(s => s.trim()).filter(Boolean).pop();
        if (this.mainProjectMap && this.mainProjectMap[raw]) return raw;
        return pathToCode[pathKey] || nameToCode[raw] || nameToCode[lastName] || null;
      }).filter(code => {
        if (!code || seen.has(code)) return false;
        seen.add(code);
        return true;
      });
    },
    async handlePushOrgChange(selectedOrgId, options) {
      const keepCampus = options && options.keepCampus;
      if (!keepCampus) {
        this.pushForm.orgCampus = '';
        this.pushForm.matchProjectCodes = [];
        this.$nextTick(() => {
          if (this.$refs.pushForm) {
            this.$refs.pushForm.clearValidate('orgCampus');
            this.$refs.pushForm.clearValidate('matchProjectCodes');
          }
        });
      }
      if (!selectedOrgId) return;
      try {
        const res = await api.post(`/org/${selectedOrgId}`, {});
        if (!(res.data && res.data.success && res.data.data)) return;
        let organCampuses = res.data.data.organCampuses;
        if (typeof organCampuses === 'string') {
          try {
            organCampuses = JSON.parse(organCampuses);
          } catch (e) {
            organCampuses = null;
          }
        }
        const campusRows = this.buildCampusRowsFromOrganCampuses(organCampuses, null);
        this.$set(this.pushCampusOptionsMap, String(selectedOrgId), campusRows);
        const mpc = res.data.data.mainProjectCodes;
        this.$set(
          this.pushOrgMainProjectCodesMap,
          String(selectedOrgId),
          this.normalizeCodeList(mpc != null ? mpc : [])
        );
        this.$nextTick(() => {
          this.syncPushMatchProjectCodes();
        });
      } catch (e) {
        console.error('加载机构校区失败', e);
      }
    },
    handlePushCampusChange() {
      this.$nextTick(() => {
        if (this.$refs.pushForm) {
          this.$refs.pushForm.clearValidate('orgCampus');
        }
      });
    },
    getValidStatusType(code) {
      const map = { VALID: 'success', INVALID: 'danger', PENDING: 'warning' };
      return map[code] || 'info';
    },
    /** 推送记录报名状态：与跟进「是否有效」同为 el-tag 色系 */
    getEnrollStatusType(code) {
      const map = { NOT_ENROLLED: 'warning', ENROLLED: 'success', REFUND: 'danger' };
      return map[code] || 'info';
    },
    /** 无 enrollStatus 时用语义名反查字典 code，保证列表/时间线着色一致 */
    resolvePushRecordEnrollCode(row) {
      if (!row) return null;
      if (row.enrollStatus) return row.enrollStatus;
      const name = (row.enrollStatusName || '').trim();
      if (!name || !this.enrollStatusOptions || !this.enrollStatusOptions.length) return null;
      const hit = this.enrollStatusOptions.find(o => o.name === name);
      return hit ? hit.code : null;
    },
    startCountdownTimer() {
      this.countdownTimer = setInterval(() => {
        this.currentTime = Date.now();
      }, 1000);
    },
    stopCountdownTimer() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    getCountdownMs(row) {
      if (!row || row.countdownSeconds === null || row.countdownSeconds === undefined) return null;
      const baseDiffMs = Number(row.countdownSeconds) * 1000;
      if (!Number.isFinite(baseDiffMs)) return null;
      const elapsedMs = Math.max(0, this.currentTime - this.listLoadedClientAt);
      return baseDiffMs - elapsedMs;
    },
    formatCountdownMs(diffMs) {
      if (diffMs === null) return '-';
      
      const absDiffMs = Math.abs(diffMs);
      const days = Math.floor(absDiffMs / (24 * 60 * 60 * 1000));
      const hours = Math.floor((absDiffMs % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      const minutes = Math.floor((absDiffMs % (60 * 60 * 1000)) / (60 * 1000));
      const seconds = Math.floor((absDiffMs % (60 * 1000)) / 1000);
      
      const timeStr = `${days}天${hours}时${minutes}分${seconds}秒`;
      
      if (diffMs < 0) {
        return `已过期 ${timeStr}`;
      }
      return timeStr;
    },
    formatCountdown(row) {
      return this.formatCountdownMs(this.getCountdownMs(row));
    },
    formatCountdownForExport(row) {
      if (!row || row.countdownSeconds === null || row.countdownSeconds === undefined) return '-';
      const diffMs = Number(row.countdownSeconds) * 1000;
      return Number.isFinite(diffMs) ? this.formatCountdownMs(diffMs) : '-';
    },
    getCountdownClass(row) {
      const diffMs = this.getCountdownMs(row);
      if (diffMs === null) return '';
      
      const oneHour = 60 * 60 * 1000;
      const threeHours = 3 * oneHour;
      const twentyFourHours = 24 * oneHour;
      
      if (diffMs < 0) {
        return 'countdown-expired';
      } else if (diffMs < oneHour) {
        return 'countdown-critical countdown-blink';
      } else if (diffMs < threeHours) {
        return 'countdown-danger';
      } else if (diffMs < twentyFourHours) {
        return 'countdown-warning';
      }
      return '';
    },
    /** 修改记录文案：将「label」由「变更前」变更为「变更后」 */
    formatBasicChangeSentence(it) {
      if (!it || !it.label) return '';
      const before = this.formatBasicChangeValue(it.before);
      const after = this.formatBasicChangeValue(it.after);
      return `将${it.label}由${before}变更为${after}`;
    },
    /** 将变更值空值/undefined 统一显示为「—」 */
    formatBasicChangeValue(v) {
      if (v === undefined || v === null || v === '') return '—';
      return String(v);
    },
    formatCityDisplay(row) {
      if (!row) return '-';
      const parts = [row.provinceName, row.cityName, row.districtName].filter(Boolean);
      if (parts.length) return parts.join('-');
      if (row.cityCodeDictName) return row.cityCodeDictName;
      return row.cityCode || '-';
    },
    formatProjectDisplay(row) {
      return row.projectCodesNames || '-';
    },
  
    async loadData() {
      this.loading = true;
      try {
        const params = {
          studentName: this.searchForm.studentName,
          phoneNumber: this.searchForm.phoneNumber,
          sourceCode: this.searchForm.sourceCode,
          orgId: this.searchForm.orgId,
          enrolledOrgId: this.searchForm.enrolledOrgId,
          validStatusCode: this.searchForm.validStatusCode,
          referrerStaffId: this.searchForm.referrerStaffId,
          page: this.currentPage,
          pageSize: this.pageSize,
          sortField: this.sortField,
          sortOrder: this.sortOrder
        };
        if (this.searchForm.projectCodes && this.searchForm.projectCodes.length > 0) {
          // 传递选中的项目代码（逗号分隔）
          const selectedCodes = this.searchForm.projectCodes.map(item => Array.isArray(item) ? item[item.length - 1] : item);
          params.projectCodes = selectedCodes.join(',');
        }
        if (this.searchForm.consultTimeRange && this.searchForm.consultTimeRange.length === 2) {
          params.consultStartTime = this.searchForm.consultTimeRange[0];
          params.consultEndTime = this.searchForm.consultTimeRange[1];
        }
        const res = await api.post('/student/list', params);
        if (res.data.success) {
          const payload = res.data.data || {};
          this.tableData = payload.list || [];
          this.total = payload.total || 0;
          this.listServerNowEpochMs = Number(payload.serverNowEpochMs || 0) || Date.now();
          this.listLoadedClientAt = Date.now();
          this.currentTime = this.listLoadedClientAt;
        }
      } catch (e) {
        this.$message.error('加载数据失败');
      }
      this.loading = false;
      this.$nextTick(() => {
        this.updateStudentTableHeight();
      });
    },
    async handleSearch() {
      this.searchLoading = true;
      this.currentPage = 1;
      await this.loadData();
      this.searchLoading = false;
    },
    handleReset() {
      this.searchForm = createDefaultSearchForm();
      this.handleSearch();
    },
    async handleExport() {
      this.exportLoading = true;
      try {
        // 优先导出勾选的行
        if (this.selectedRows.length > 0) {
          if (this.selectedRows.length > 1000) {
            this.$message.warning('单次导出不能超过1000条数据，请减少勾选数量');
            this.exportLoading = false;
            return;
          }
          this.exportToExcel(this.selectedRows);
          this.exportLoading = false;
          return;
        }
        // 没有勾选则按查询条件导出
        const params = {
          studentName: this.searchForm.studentName,
          phoneNumber: this.searchForm.phoneNumber,
          sourceCode: this.searchForm.sourceCode,
          orgId: this.searchForm.orgId,
          enrolledOrgId: this.searchForm.enrolledOrgId,
          validStatusCode: this.searchForm.validStatusCode,
          referrerStaffId: this.searchForm.referrerStaffId,
          page: 1,
          pageSize: 1001,
          sortField: this.sortField,
          sortOrder: this.sortOrder
        };
        if (this.searchForm.projectCodes && this.searchForm.projectCodes.length > 0) {
          const selectedCodes = this.searchForm.projectCodes.map(item => Array.isArray(item) ? item[item.length - 1] : item);
          params.projectCodes = selectedCodes.join(',');
        }
        if (this.searchForm.consultTimeRange && this.searchForm.consultTimeRange.length === 2) {
          params.consultStartTime = this.searchForm.consultTimeRange[0];
          params.consultEndTime = this.searchForm.consultTimeRange[1];
        }
        const res = await api.post('/student/list', params);
        if (res.data.success) {
          const list = res.data.data.list || [];
          if (list.length > 1000) {
            this.$message.warning('单次导出不能超过1000条数据，请缩小查询范围');
            this.exportLoading = false;
            return;
          }
          if (list.length === 0) {
            this.$message.warning('没有符合条件的数据可导出');
            this.exportLoading = false;
            return;
          }
          this.exportToExcel(list);
        }
      } catch (e) {
        this.$message.error('导出失败');
      }
      this.exportLoading = false;
    },
    exportToExcel(data) {
      const XLSX = require('xlsx');
      const exportData = data.map((row, index) => ({
        '序号': index + 1,
        '学员姓名': row.studentName || '',
        '手机号': row.phoneNumber || '',
        '来源渠道': row.sourceName || row.sourceCode || '',
        '咨询课程': this.formatProjectDisplay(row),
        '所在城市': this.formatCityDisplay(row),
        '是否有效': row.validStatusName || row.validStatusCode || '',
        '推荐人': row.referrerName || '',
        '回访倒计时': this.formatCountdown(row),
        '备注': row.remark || '',
        '创建时间': row.createTime || ''
      }));
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '学生清单');
      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
      const fileName = `学生清单${data.length}-${dateStr}.xlsx`;
      XLSX.writeFile(wb, fileName);
      this.$message.success(`成功导出 ${data.length} 条数据`);
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.loadData();
    },
    handleCurrentChange(page) {
      this.currentPage = page;
      this.loadData();
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows;
    },
    handleViewModeChange() {
      this.selectedRows = [];
      this.$nextTick(() => {
        this.updateStudentTableHeight();
      });
    },
    handleSortChange({ prop, order }) {
      if (prop) {
        this.sortField = prop;
        this.sortOrder = order === 'ascending' ? 'asc' : 'desc';
      } else {
        this.sortField = 'createTime';
        this.sortOrder = 'desc';
      }
      this.loadData();
    },
    async handleAdd() {
      this.dialogTitle = '新增学生';
      this.isEdit = false;
      this.editId = null;
      this.form.referrerStaffId = '';
      this.form.referrerName = this.getCurrentUserDisplayName();
      try {
        const res = await api.post('/staff/current', {});
        if (res.data && res.data.success && res.data.data) {
          this.form.referrerStaffId = res.data.data.staffId;
          this.form.referrerName = res.data.data.staffName || res.data.data.phoneNumber || this.form.referrerName;
        }
      } catch (e) {
        // 当前账号未关联会员，referrerStaffId 为空，需在表单中选择推荐人
      }
      await this.loadReferrerStaffOptions();
      this.dialogVisible = true;
    },
    async loadReferrerStaffOptions() {
      this.referrerStaffLoading = true;
      try {
        const res = await api.post('/staff/list', { page: 1, pageSize: 500 });
        if (res.data && res.data.success && res.data.data) {
          this.referrerStaffOptions = res.data.data.list || [];
        }
      } catch (e) {
        console.error('加载推荐人列表失败', e);
      }
      this.referrerStaffLoading = false;
    },
    addContactRow() {
      if (this.form.contacts.length >= 3) return;
      this.form.contacts.push({ phone: '', wechat: '' });
    },
    removeContactRow(index) {
      if (this.form.contacts.length <= 1) return;
      this.form.contacts.splice(index, 1);
    },
    handleEdit(row) {
      if (
        row
        && !this.isPrivilegedStudentEditor
        && (row.enrollStatusCode === 'REFUND' || row.validStatusCode === 'INVALID')
      ) {
        this.$message.warning('该学生已经被冻结，请联系管理员！');
        return;
      }
      this.dialogTitle = '编辑学生';
      this.isEdit = true;
      this.editId = row.id;
      
      // 解析 projectCodes
      const projectCodes = row.projectCodes ? (typeof row.projectCodes === 'string' ? JSON.parse(row.projectCodes) : row.projectCodes) : [];
      
      // 取叶子节点的 code（优先级：districtCode > cityCodeRegion > provinceCode）
      const cityCode = row.districtCode || row.cityCodeRegion || row.provinceCode || '';
      
      const phoneNumbers = Array.isArray(row.phoneNumbers)
        ? row.phoneNumbers
        : (row.phoneNumber ? [row.phoneNumber] : []);
      const wechatNumbers = Array.isArray(row.wechatNumbers) ? row.wechatNumbers : [];
      const rowCount = Math.max(phoneNumbers.length, wechatNumbers.length, 1);
      const contacts = [];
      for (let i = 0; i < Math.min(3, rowCount); i++) {
        contacts.push({
          phone: phoneNumbers[i] || '',
          wechat: wechatNumbers[i] || ''
        });
      }

      this.form = {
        studentName: row.studentName,
        contacts,
        sourceCode: row.sourceCode || '',
        projectCodes: projectCodes,
        cityCode: cityCode,
        referrerStaffId: row.referrerStaffId || '',
        referrerName: row.referrerName || row.referrerStaffId || this.getCurrentUserDisplayName(),
        addressDetail: row.addressDetail || '',
        remark: row.remark || '',
        validStatusCode: row.validStatusCode || 'PENDING'
      };
      this.dialogVisible = true;
    },
    findProjectParent(code) {
      for (const parent of this.projectCascaderOptions) {
        if (parent.children) {
          const child = parent.children.find(c => c.value === code);
          if (child) {
            return { parentCode: parent.value, code: child.value };
          }
        }
        if (parent.value === code) {
          return null;
        }
      }
      return null;
    },
    async handleDetail(row) {
      this.detailData = row;
      this.detailNeedRefreshList = false;
      this.activeTab = 'push';
      this.loadDetailRecordsViewPreferences();
      this.detailVisible = true;
      await this.loadOrgOptions();
      await this.loadPushRecords(row.id);
      await this.loadFollowRecords(row.id);
      await this.loadBasicChangeLogs(row.id);
    },
    async loadBasicChangeLogs(studentId) {
      try {
        const res = await api.post(`/student/${studentId}/basic-change-logs`, {});
        if (res.data.success) {
          this.basicChangeLogs = res.data.data || [];
        } else {
          this.basicChangeLogs = [];
        }
      } catch (e) {
        console.error('加载修改记录失败', e);
        this.basicChangeLogs = [];
      }
    },
    async handleDetailTabClick(tab) {
      // el-tabs tab-click 事件：tab.name 对应 el-tab-pane 的 name
      const tabName = tab && tab.name;
      if (!tabName || !this.detailData || !this.detailData.id) return;
      const studentId = this.detailData.id;

      // 切换时只刷新当前 tab，避免不必要的多接口并发
      if (tabName === 'push') {
        await this.loadPushRecords(studentId);
      } else if (tabName === 'follow') {
        await this.loadFollowRecords(studentId);
      } else if (tabName === 'history') {
        await this.loadBasicChangeLogs(studentId);
      }
    },
    async loadPushRecords(studentId) {
      try {
        const res = await api.post(`/student/${studentId}/push-records/query`, {});
        if (res.data.success) {
          this.pushRecords = res.data.data || [];
        }
      } catch (e) {
        console.error('加载推送记录失败', e);
      }
    },
    async loadFollowRecords(studentId) {
      try {
        const res = await api.post(`/student/${studentId}/follow-records/query`, {});
        if (res.data.success) {
          this.followRecords = res.data.data || [];
        }
      } catch (e) {
        console.error('加载跟进记录失败', e);
      }
    },
    closeDetail() {
      if (this.detailNeedRefreshList) {
        this.loadData();
      }
      this.detailData = null;
      this.detailNeedRefreshList = false;
      this.pushRecords = [];
      this.followRecords = [];
      this.basicChangeLogs = [];
      this.activeTab = 'push';
    },
    showPushForm() {
      if (!this.canShowStudentWriteActions) {
        this.$message.warning('当前角色无权限添加推送记录');
        return;
      }
      if (this.isDetailStudentInvalid) {
        this.$message.warning('该学员已标记为无效，无法添加推送记录');
        return;
      }
      if (!this.followRecords || this.followRecords.length === 0) {
        this.$message.warning('请先添加跟进记录，方可添加推送记录');
        return;
      }
      const account = this.getCurrentUserDisplayName();
      this.pushFormMode = 'add';
      this.editingPushRecordId = null;
      this.editingPushOriginalOrderStatusCode = '';
      this.editingPushOrderNo = '';
      this.originalPushEnrollStatus = '';
      this.pushForm = {
        orgId: '',
        orgCampus: '',
        matchProjectCodes: [],
        projectRemark: '',
        submitMethod: '',
        pushUserName: account,
        orderStatusCode: 'TO_SCHOOL',
        enrollStatus: 'NOT_ENROLLED',
        payAmount: null,
        commissionAmount: null,
        refundAmount: null,
        refundCommissionAmount: null
      };
      this.normalizePushOrderStatusByRole();
      this.orgQueryKeyword = '';
      this.orgQueryPage = 1;
      this.orgQueryTotal = 0;
      this.loadOrgOptions(1, '');
      this.pushFormVisible = true;
      this.$nextTick(() => {
        if (this.$refs.pushForm) this.$refs.pushForm.clearValidate();
      });
    },
    async showPushEditForm(row) {
      if (!row || !row.id || !this.detailData) return;
      if (this.isDetailStudentInvalid) {
        this.$message.warning('该学员已标记为无效，无法编辑推送记录');
        return;
      }
      this.pushFormMode = 'edit';
      this.editingPushRecordId = row.id;
      this.editingPushOriginalOrderStatusCode = row.orderStatusCode || '';
      this.editingPushOrderNo = row.orderNo || '';
      const enrollCode = this.resolvePushRecordEnrollCode(row) || 'NOT_ENROLLED';
      this.originalPushEnrollStatus = enrollCode;
      const shouldFillDealAmounts = enrollCode === 'ENROLLED' || this.isFinanceRole;
      const shouldFillRefundAmounts = enrollCode === 'REFUND' || this.isFinanceRole;
      const payN = row.payAmount != null && row.payAmount !== '' ? Number(row.payAmount) : null;
      const comN = row.commissionAmount != null && row.commissionAmount !== '' ? Number(row.commissionAmount) : null;
      const refN = row.refundAmount != null && row.refundAmount !== '' ? Number(row.refundAmount) : null;
      const refComN = row.refundCommissionAmount != null && row.refundCommissionAmount !== '' ? Number(row.refundCommissionAmount) : null;

      this.pushForm = {
        orgId: row.orgId || '',
        orgCampus: '',
        matchProjectCodes: this.matchProjectTextToCodes(row.matchProject),
        projectRemark: row.projectRemark || '',
        submitMethod: row.submitMethod || '',
        pushUserName: row.pushUserName || this.getCurrentUserDisplayName(),
        // 编辑弹窗必须回显“当前行推送记录”的状态快照，禁止回退到学生级状态
        orderStatusCode: row.orderStatusCode || 'TO_SCHOOL',
        enrollStatus: enrollCode,
        payAmount: shouldFillDealAmounts ? payN : null,
        commissionAmount: shouldFillDealAmounts ? comN : null,
        refundAmount: shouldFillRefundAmounts ? refN : null,
        refundCommissionAmount: shouldFillRefundAmounts ? refComN : null
      };
      this.syncPushEnrollStatusByOrderStatus(false);

      this.orgQueryKeyword = '';
      this.orgQueryPage = 1;
      this.orgQueryTotal = 0;
      await this.loadOrgOptions(1, '');
      await this.handlePushOrgChange(this.pushForm.orgId, { keepCampus: true });
      this.pushForm.orgCampus = row.orgCampus || '';

      this.pushFormVisible = true;
      this.$nextTick(() => {
        if (this.$refs.pushForm) this.$refs.pushForm.clearValidate();
      });
    },
    syncPushEnrollStatusByOrderStatus(showAlert = true) {
      const orderStatus = this.pushForm && this.pushForm.orderStatusCode ? String(this.pushForm.orderStatusCode).trim() : '';
      if (orderStatus === 'REFUND') {
        this.pushForm.enrollStatus = 'REFUND';
        return;
      }
      if (this.pushForm.enrollStatus === 'REFUND') {
        this.pushForm.enrollStatus = 'NOT_ENROLLED';
      }
      if (!this.isFinanceRole) {
        this.pushForm.refundAmount = null;
        this.pushForm.refundCommissionAmount = null;
      }
    },
    isPushOrderStatusOptionDisabled(item) {
      const code = item && item.code ? String(item.code).trim() : '';
      if (this.isFinanceEditOrderCreatedRestricted) {
        return !['SETTLED', 'REFUND'].includes(code);
      }
      if (!this.isFinanceRole) {
        return !['TO_SCHOOL', 'SCHOOL_PAID', 'INVALID'].includes(code);
      }
      return false;
    },
    formatPushOrderStatusOptionLabel(item) {
      const name = item && item.name ? String(item.name).trim() : '';
      if (!name) return '';
      return this.isPushOrderStatusOptionDisabled(item) ? `${name}（无权限）` : name;
    },
    normalizePushOrderStatusByRole() {
      if (this.isFinanceEditOrderCreatedRestricted) {
        const code = this.pushForm && this.pushForm.orderStatusCode ? String(this.pushForm.orderStatusCode).trim() : '';
        if (!['SETTLED', 'REFUND'].includes(code)) {
          this.pushForm.orderStatusCode = 'SETTLED';
        }
        return;
      }
      if (!this.isFinanceRole) {
        const code = this.pushForm && this.pushForm.orderStatusCode ? String(this.pushForm.orderStatusCode).trim() : '';
        if (!['TO_SCHOOL', 'SCHOOL_PAID', 'INVALID'].includes(code)) {
          this.pushForm.orderStatusCode = 'TO_SCHOOL';
        }
      }
    },
    onPushOrderStatusChange() {
      this.normalizePushOrderStatusByRole();
      this.syncPushEnrollStatusByOrderStatus(true);
    },
    async submitPushRecord(options = {}) {
      if (this.pushSubmitLoading) return;
      this.pushSubmitLoading = true;
      try {
        const forceCreateOrder = !!(options && options.forceCreateOrder);
        this.normalizePushOrderStatusByRole();
        this.syncPushEnrollStatusByOrderStatus(false);
        await this.$refs.pushForm.validate();
        const matchProject = (this.pushForm.matchProjectCodes && this.pushForm.matchProjectCodes.length)
          ? this.pushForm.matchProjectCodes.map(c => this.mainProjectMap[c] || c).join('、')
          : '';
        const submitData = { ...this.pushForm, matchProject };
        if (forceCreateOrder) submitData.forceCreateOrder = true;
        delete submitData.matchProjectCodes;
        delete submitData.pushTime;
        const isEdit = !!this.editingPushRecordId;
        const res = isEdit
          ? await api.post(`/student/${this.detailData.id}/push-records/${this.editingPushRecordId}/update`, submitData)
          : await api.post(`/student/${this.detailData.id}/push-records`, submitData);
        if (res.data.success) {
          const returnedOrderNo = res.data && res.data.data && res.data.data.orderNo
            ? String(res.data.data.orderNo).trim()
            : '';
          const becameEnrolled = this.pushForm.enrollStatus === 'ENROLLED'
            && (this.pushFormMode === 'add' || this.originalPushEnrollStatus !== 'ENROLLED');
          const shouldAlertOrderCreated = !!returnedOrderNo && (becameEnrolled || forceCreateOrder);
          this.$message.success(isEdit ? '保存成功' : '添加成功');
          if (shouldAlertOrderCreated) {
            setTimeout(() => {
              this.$alert(
                `已自动创建订单（订单编号：${returnedOrderNo}）`,
                '提示',
                { type: 'success', confirmButtonText: '知道了' }
              );
            }, 220);
          }
          if (returnedOrderNo && this.detailData) {
            this.$set(this.detailData, 'orderNo', returnedOrderNo);
          }
          if (this.detailData) {
            const selectedOrderStatus = this.orderStatusOptions.find(v => v.code === this.pushForm.orderStatusCode);
            this.$set(this.detailData, 'orderStatusCode', this.pushForm.orderStatusCode);
            this.$set(this.detailData, 'orderStatusName', selectedOrderStatus ? selectedOrderStatus.name : this.pushForm.orderStatusCode);
          }
          this.detailNeedRefreshList = true;
          this.pushFormVisible = false;
          await this.loadPushRecords(this.detailData.id);
        } else {
          this.$message.error(res.data.message || (isEdit ? '保存失败' : '添加失败'));
        }
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message;
        this.$message.error(msg || (this.editingPushRecordId ? '保存失败' : '添加失败'));
      } finally {
        this.pushSubmitLoading = false;
      }
    },
    /** 仅同步「下次联系」选中日历日期后的默认时分秒为当前客户端时间，不填年月日 */
    syncFollowNextPickerDefaultTimeFromNow() {
      const n = new Date();
      const pad = (x) => String(x).padStart(2, '0');
      this.followNextPickerDefaultTime = `${pad(n.getHours())}:${pad(n.getMinutes())}:${pad(n.getSeconds())}`;
    },
    showFollowForm() {
      if (!this.canShowStudentWriteActions) {
        this.$message.warning('当前角色无权限添加跟进记录');
        return;
      }
      if (this.isDetailStudentInvalid) {
        this.$message.warning('该学员已标记为无效，无法添加跟进记录');
        return;
      }
      if (this.isDetailRefundFrozen) {
        this.$message.warning('该学员已退费，已被冻结，请联系管理员操作');
        return;
      }
      const account = this.getCurrentUserDisplayName();
      const now = new Date();
      this.syncFollowNextPickerDefaultTimeFromNow();
      this.followForm = {
        followTime: now,
        nextContactTime: null,
        followUserName: account,
        followContent: DEFAULT_FOLLOW_CONTENT,
        validStatusCode: (this.detailData && this.detailData.validStatusCode) || 'PENDING',
        stopFollowUp: false
      };
      this.followSubmitLoading = false;
      this.followFormVisible = true;
    },
    onStopFollowUpChange(checked) {
      if (checked) {
        this.followForm.nextContactTime = null;
        this.$nextTick(() => {
          if (this.$refs.followForm) this.$refs.followForm.clearValidate('nextContactTime');
        });
      } else if (this.followForm.validStatusCode !== 'INVALID' && !this.followForm.nextContactTime) {
        this.syncFollowNextPickerDefaultTimeFromNow();
      }
    },
    /** 字典 STUDENT_VALID_STATUS 中「无效」对应 code 为 INVALID */
    onFollowValidStatusChange(val) {
      if (val === 'INVALID') {
        this.followForm.nextContactTime = null;
        this.$nextTick(() => {
          if (this.$refs.followForm) this.$refs.followForm.clearValidate('nextContactTime');
        });
        this.$alert(
          '标记为无效后，该学员将被冻结，无法继续跟进和推送，只能重新录入！',
          '提示',
          { type: 'warning', confirmButtonText: '我知道了' }
        );
      } else if (!this.followForm.nextContactTime) {
        this.syncFollowNextPickerDefaultTimeFromNow();
      }
    },
    async submitFollowRecord() {
      if (this.followSubmitLoading) return;
      this.followSubmitLoading = true;
      try {
        await this.$refs.followForm.validate();
      } catch (e) {
        this.followSubmitLoading = false;
        return;
      }
      const account = this.getCurrentUserDisplayName();
      try {
        const { stopFollowUp, ...followPayload } = this.followForm;
        const res = await api.post(`/student/${this.detailData.id}/follow-records`, {
          ...followPayload,
          followUserName: this.followForm.followUserName || account
        });
        if (res.data.success) {
          this.$message.success('添加成功');
          this.followFormVisible = false;
          this.detailData.validStatusCode = this.followForm.validStatusCode;
          const selected = this.validStatusOptions.find(v => v.code === this.followForm.validStatusCode);
          this.detailData.validStatusName = selected ? selected.name : this.followForm.validStatusCode;
          this.detailNeedRefreshList = true;
          await this.loadFollowRecords(this.detailData.id);
        } else {
          this.$message.error(res.data.message || '添加失败');
        }
      } catch (e) {
        this.$message.error('添加失败');
      } finally {
        this.followSubmitLoading = false;
      }
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定删除该学生吗？', '提示', { type: 'warning' });
        this.deleteLoading = true;
        const res = await api.post(`/student/${row.id}/remove`);
        if (res.data.success) {
          this.$message.success('删除成功');
          this.loadData();
        } else {
          this.$message.error(res.data.message || '删除失败');
        }
      } catch (e) {
        if (e !== 'cancel') this.$message.error('删除失败');
      } finally {
        this.deleteLoading = false;
      }
    },
    async handleBatchDelete() {
      if (!this.selectedRows.length) return;
      try {
        await this.$confirm(`确定删除选中的 ${this.selectedRows.length} 条记录吗？`, '提示', { type: 'warning' });
        this.batchDeleteLoading = true;
        let successCount = 0;
        for (const row of this.selectedRows) {
          try {
            const res = await api.post(`/student/${row.id}/remove`);
            if (res.data.success) successCount++;
          } catch (e) {
            console.error('删除失败', e);
          }
        }
        this.$message.success(`成功删除 ${successCount} 条记录`);
        this.loadData();
      } catch (e) {
        if (e !== 'cancel') this.$message.error('批量删除失败');
      } finally {
        this.batchDeleteLoading = false;
      }
    },
    async handleSubmit() {
      try {
        await this.$refs.form.validate();
      } catch (e) {
        this.$nextTick(() => {
          const errorEl = document.querySelector('.el-form-item.is-error');
          if (errorEl) {
            errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return;
      }
      this.submitLoading = true;
      try {
        const projectCodesArray = this.form.projectCodes.map(item => {
          return Array.isArray(item) ? item[item.length - 1] : item;
        });
        const contacts = (this.form.contacts || []).map(item => ({
          phone: (item.phone || '').trim(),
          wechat: (item.wechat || '').trim()
        }));
        const phoneNumbers = contacts.map(item => item.phone).filter(Boolean);
        const wechatNumbers = contacts.map(item => item.wechat).filter(Boolean);
        
        // 从叶子节点 code 获取完整路径
        const regionPath = this.$refs.regionCascader ? this.$refs.regionCascader.getFullPath(this.form.cityCode) : [];
        const provinceCode = regionPath[0] ? regionPath[0].code : '';
        const cityCodeRegion = regionPath[1] ? regionPath[1].code : '';
        const districtCode = regionPath[2] ? regionPath[2].code : '';
        
        const submitData = {
          studentName: this.form.studentName,
          phoneNumber: phoneNumbers[0] || '',
          phoneNumbers,
          wechatNumbers,
          sourceCode: this.form.sourceCode,
          projectCodes: projectCodesArray,
          cityCode: this.form.cityCode || '',
          provinceCode: provinceCode,
          cityCodeRegion: cityCodeRegion,
          districtCode: districtCode,
          addressDetail: this.form.addressDetail,
          remark: this.form.remark,
          validStatusCode: this.form.validStatusCode,
          referrerStaffId: this.form.referrerStaffId || null
        };
        
        let res;
        if (this.isEdit) {
          res = await api.post(`/student/${this.editId}/update`, submitData);
        } else {
          res = await api.post('/student/add', submitData);
        }
        if (res.data.success) {
          this.$message.success(this.isEdit ? '更新成功' : '新增成功');
          this.dialogVisible = false;
          this.loadData();
          if (this.isEdit && this.detailVisible && this.detailData && this.detailData.id === this.editId) {
            await this.loadBasicChangeLogs(this.editId);
          }
        } else {
          this.$message.error(res.data.message || '操作失败');
        }
      } catch (e) {
        const backendMsg = (e && e.response && e.response.data && e.response.data.message) || '';
        this.$message.error(backendMsg || '操作失败');
      }
      this.submitLoading = false;
    },
    resetForm() {
      this.form = {
        studentName: '',
        contacts: [{ phone: '', wechat: '' }],
        sourceCode: '',
        projectCodes: [],
        cityCode: '',
        referrerStaffId: '',
        referrerName: this.getCurrentUserDisplayName(),
        addressDetail: '',
        remark: '',
        validStatusCode: 'PENDING'
      };
      this.$refs.form && this.$refs.form.resetFields();
    }
  }
};
</script>

<style scoped>
.student-manage {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-bottom: 56px;
}

.student-manage .search-form .el-input{
  width:200px;
}
.student-manage .search-form .operate_btn_box{
  width:100%;
  margin-bottom:0;
}
.student-manage .search-form .operate_btn_box >>> .el-form-item__content{
  width:100%;
  display:flex;
  justify-content:space-between;
  align-items:center;
}
.student-manage .search-form .operate_btn_box >>> .el-form-item__content .left_box{
  flex:1;
}
.student-manage .search-form .operate_btn_box >>> .el-form-item__content .right_box{
  display:flex;
  width:200px;
}
.student-manage .search-area{
  margin-bottom:0;
}
.search-area {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.search-form {
  width: 100%;
}
.search-form >>> .el-range-separator{
  width:7%;
}


.operation-header-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.column-config-trigger {
  padding: 0;
  font-size: 16px;
}

.column-config-tip {
  margin-bottom: 10px;
  color: #909399;
  font-size: 13px;
}

.column-config-list {
  max-height: 420px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 8px 10px;
}

.column-config-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  border-bottom: 1px dashed #f0f0f0;
  cursor: move;
}

.column-config-item:last-child {
  border-bottom: none;
}

.column-drag-icon {
  margin-left: auto;
  color: #909399;
  cursor: move;
}

.column-fixed-tip {
  margin-left: auto;
  font-size: 12px;
  color: #67c23a;
}

.table-card {
  margin-top: 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.table-card >>> .el-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}

.table-card >>> .el-table {
  flex: 1;
  display: block !important;
}

.student-card-wrapper {
  padding: 10px;
  min-height: 200px;
  overflow: auto !important;
}

.student-card-item {
  margin-bottom: 12px;
  position: relative;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.student-card-item:hover {
  transform: translateY(-3px);
  border-color: #d9ecff;
  box-shadow: 0 8px 18px rgba(64, 158, 255, 0.16) !important;
}

.student-card-item-unpushed {
  border-color: #f56c6c;
  animation: unpushedCardBreath 1.8s ease-in-out infinite;
}

.student-card-item.student-card-item-unpushed:hover {
  border-color: #f56c6c;
  box-shadow: 0 10px 22px rgba(245, 108, 108, 0.28) !important;
}

@keyframes unpushedCardBreath {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(245, 108, 108, 0.12) !important;
  }
  50% {
    box-shadow: 0 8px 18px rgba(245, 108, 108, 0.3) !important;
  }
}

.student-card-status {
  font-size: 12px;
  line-height: 16px;
  padding: 0;
  border-radius: 0;
  position: absolute;
  top: 12px;
  right: 14px;
}

.student-card-status-pushed {
  color: #67c23a;
  background: transparent;
}

.student-card-status-unpushed {
  color: #f56c6c;
  background: transparent;
}

.student-card-item-unpushed .student-card-status-unpushed {
  opacity: 1;
  transform: translateY(0);
  transition: opacity 1s ease, transform 1s ease;
}

.student-card-item-unpushed:hover .student-card-status-unpushed {
  opacity: 0;
  transform: translateY(-14px);
}

.student-card-item >>> .el-card__body {
  padding: 14px;
}

.student-card-line {
  display: flex;
  align-items: center;
  line-height: 1.8;
  margin-bottom: 3px;
}

.student-card-label {
  width: 76px;
  color: #a8afb9;
  font-size: 12px;
  flex: 0 0 76px;
}

.student-card-value {
  color: #303133;
  font-size: 14px;
  word-break: break-all;
}

.student-card-time-line {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #f4f6f8;
  font-size: 12px;
  color: #b7bec8;
}

.danger-btn {
  color: #F56C6C;
}

.danger-btn:hover {
  color: #f78989;
}

.student-push-tag-pushed {
  color: #67c23a;
}

.student-push-tag-unpushed {
  color: #f56c6c;
}

.countdown-expired {
  color: #F56C6C;
}

.countdown-warning {
  color: #E6A23C;
}

.countdown-danger {
  color: #F56C6C;
}

.countdown-critical {
  color: #F56C6C;
}

.countdown-blink {
  animation: blink 1s ease-in-out infinite;
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}

.detail-dialog .el-dialog__body {
  display: flex;
  flex-direction: column;
  min-height: 90vh;
  max-height: 95vh;
  padding: 20px;
  overflow: hidden;
  box-sizing: border-box;
}

.detail-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.detail-content > .student-basic-desc {
  flex-shrink: 0;
}

.time-cell {
  line-height: 1.3;
  font-size: 13px;
}

.time-cell .time-part {
  color: #606266;
  font-size: 13px;
}

.detail-content >>> .student-basic-desc .el-descriptions-item__container {
  display: flex;
  width: 100%;
}

.detail-content >>> .student-basic-desc .el-descriptions-item__label,
.detail-content >>> .student-basic-desc .el-descriptions-item__content {
  flex: 0 0 50%;
  max-width: 50%;
  box-sizing: border-box;
}

.detail-content >>> .student-basic-desc .el-descriptions-item__content {
  text-align: left;
}

.detail-content >>> .student-basic-desc .el-descriptions-item__label {
  text-align: right;
  min-width:80px;
}

.push-record-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
}

.push-records-table >>> .el-table__body-wrapper {
  min-height: 200px;
}

.push-records-table >>> td.push-records-money-cell {
  color: #409eff;
}

.follow-records-table >>> .el-table__body-wrapper {
  min-height: 200px;
}

/* 学生详情 Tab：占满基本信息以下剩余高度 */
.student-detail-tabs-wrap {
  margin-top: 16px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.student-detail-tabs-wrap .student-detail-tabs {
  margin-top: 0;
}

.student-detail-tabs {
  margin-top: 0;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.student-detail-tabs >>> .el-tabs__header {
  flex-shrink: 0;
}

.student-detail-tabs >>> .el-tabs__content {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.student-detail-tabs >>> .el-tab-pane {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.student-detail-tabs >>> .el-tab-pane > .detail-tab-toolbar {
  flex-shrink: 0;
}

.student-detail-tabs >>> .el-tab-pane > .push-records-table,
.student-detail-tabs >>> .el-tab-pane > .follow-records-table {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.student-detail-tabs >>> .el-tab-pane > .apple-timeline-wrap {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.student-detail-tabs >>> .el-tabs__item {
  text-align: center;
}

.student-detail-tabs >>> .el-tabs__nav .el-tabs__item {
  text-align: center;
  padding: 0 20px;
}

/* 推送 / 跟进 / 修改记录 Tab 内 Descriptions：标签文字分散（两端）对齐 */
.student-detail-tabs >>> .el-descriptions-item__label {
  text-align: justify;
  text-align-last: justify;
  text-justify: inter-ideograph;
}

/* 无边框模式：标签在 flex 容器内，固定宽度后「分散对齐」才生效（与 pushTimelineDescLabelStyle.minWidth 一致） */
.student-detail-tabs >>> .el-descriptions-item__container .el-descriptions-item__label {
  flex: 0 0 72px;
  width: 72px;
  box-sizing: border-box;
}

.detail-tab-toolbar {
  margin-bottom: 12px;
}

.detail-tab-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.detail-tab-view-switch {
  margin-left: auto;
}

/* Apple-style vertical timeline */
.apple-timeline-wrap {
  min-height: 200px;
  padding: 4px 0 20px;
}

.apple-timeline {
  position: relative;
  max-width: 920px;
  margin: 0 auto;
}

.apple-timeline-row {
  display: flex;
  align-items: stretch;
  gap: 0;
}

.apple-timeline-axis {
  position: relative;
  width: 20px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.apple-timeline-dot {
  width: 12px;
  height: 12px;
  margin-top: 22px;
  border-radius: 50%;
  background: linear-gradient(180deg, #f5f5f7 0%, #e8e8ed 100%);
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

/* 时间线最新节点（列表首条，按创建时间倒序） */
.apple-timeline-dot.apple-timeline-dot--latest {
  background: #8cc5ff;
  box-shadow: 0 0 0 1px #8cc5ff, 0 2px 8px #8cc5ff;
}

.apple-timeline-connector {
  flex: 1;
  width: 2px;
  min-height: 28px;
  margin: 4px 0 0;
  border-radius: 1px;
  background: linear-gradient(180deg, rgba(60, 60, 67, 0.15) 0%, rgba(60, 60, 67, 0.06) 100%);
}

.apple-timeline-card {
  flex: 1;
  min-width: 0;
  margin-left: 14px;
  margin-bottom: 20px;
  padding: 16px 18px 14px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.045);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.apple-timeline-card__time {
  font-size: 12px;
  font-weight: 600;
  color: rgba(60, 60, 67, 0.55);
  letter-spacing: 0.02em;
  margin-bottom: 4px;
}

.apple-timeline-card__title {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.022em;
  color: #1d1d1f;
  line-height: 1.25;
  margin-bottom: 12px;
}

/* 推送 / 跟进记录时间线：Descriptions 无行间边框，column=6 与 span 组合排版 */
.apple-timeline-card .push-timeline-desc,
.apple-timeline-card .follow-timeline-desc,
.apple-timeline-card .history-timeline-desc {
  margin-top: 0;
}

.push-timeline-desc >>> .el-descriptions__body .el-descriptions__table,
.follow-timeline-desc >>> .el-descriptions__body .el-descriptions__table,
.history-timeline-desc >>> .el-descriptions__body .el-descriptions__table {
  width: 100%;
  border-collapse: collapse;
}

.push-timeline-desc >>> .el-descriptions__body .el-descriptions__table th,
.push-timeline-desc >>> .el-descriptions__body .el-descriptions__table td,
.follow-timeline-desc >>> .el-descriptions__body .el-descriptions__table th,
.follow-timeline-desc >>> .el-descriptions__body .el-descriptions__table td,
.history-timeline-desc >>> .el-descriptions__body .el-descriptions__table th,
.history-timeline-desc >>> .el-descriptions__body .el-descriptions__table td {
  border: none !important;
  box-shadow: none !important;
  vertical-align: top;
}

.push-timeline-desc >>> .push-desc-cell--push-user,
.push-timeline-desc >>> .push-desc-cell--commission {
  text-align: right;
}

.push-timeline-desc >>> .el-descriptions-item__content.push-desc-remark-cell,
.follow-timeline-desc >>> .el-descriptions-item__content.follow-desc-content-cell,
.history-timeline-desc >>> .el-descriptions-item__content.follow-desc-content-cell {
  white-space: pre-wrap;
  word-break: break-word;
  color: #1d1d1f;
}

.history-timeline-desc >>> .el-descriptions-item__content.basic-change-content-cell {
  white-space: normal;
  word-break: break-word;
  color: #1d1d1f;
}

.basic-change-line + .basic-change-line {
  margin-top: 8px;
}

/* 变更记录：变更前/变更后字号比正文增加 */
.basic-change-value-big {
  font-size: calc(1em + 2pt);
}

.basic-change-line {
  display: block;
  width: 100%;
  word-break: break-word;
}

.basic-change-list {
  margin: 0;
  padding-left: 18px;
}

.basic-change-list li {
  word-break: break-word;
}

.basic-change-list li + li {
  margin-top: 8px;
}

.apple-timeline-kv {
  --timeline-kv-label-width: 56px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.apple-timeline-kv__row {
  display: flex;
  align-items: flex-start;
  font-size: 14px;
  line-height: 1.4;
}

.apple-timeline-kv__row .k {
  flex: 0 0 var(--timeline-kv-label-width);
  width: var(--timeline-kv-label-width);
  box-sizing: border-box;
  color: rgba(60, 60, 67, 0.55);
  font-weight: 500;
}

.apple-timeline-kv__row .v {
  flex: 1;
  min-width: 0;
  color: #1d1d1f;
  word-break: break-word;
}

/* 推送记录时间线：两列 / 三列同行均分宽度，左对齐 */
.apple-timeline-kv__row--split2,
.apple-timeline-kv__row--split3 {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 12px;
}

/* 机构校区与匹配项目（匹配项目随机构品牌）、提交方式+推送人、退费场景的报名状态+退费金额：两格各占整行约 1/3 宽，左对齐，右侧留白 */
.apple-timeline-kv__row--pair-each-third .apple-timeline-kv__pair {
  flex: 0 0 33.333333% !important;
  max-width: 33.333333%;
  min-width: 0;
}

.apple-timeline-kv__pair {
  flex: 1 1 0;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px 8px;
  text-align: left;
}

.apple-timeline-kv__pair .k {
  flex: 0 0 var(--timeline-kv-label-width) !important;
  width: var(--timeline-kv-label-width) !important;
  max-width: var(--timeline-kv-label-width);
  box-sizing: border-box;
  margin-bottom: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.apple-timeline-kv__pair .v {
  flex: 1 1 0;
  min-width: 0;
  width: auto;
  text-align: left;
}

.push-timeline-desc .apple-timeline-kv__money-v {
  color: #409eff !important;
}

/* 返佣、推送人：pair 内 label 与内容分散（两端）对齐 */
.apple-timeline-kv__pair--between {
  justify-content: space-between;
  align-items: center;
}

.apple-timeline-kv__pair--between .v {
  flex: 0 1 auto !important;
  text-align: right;
  min-width: 0;
}

.apple-timeline-kv__row--between {
  justify-content: space-between;
  align-items: center;
}

.apple-timeline-kv__row--between .v {
  flex: 0 1 auto;
  text-align: right;
}

/* 备注：整行分散对齐，多行正文左对齐 */
.apple-timeline-kv__row--remark {
  padding-top: 8px;
  margin-top: 4px;
}

.apple-timeline-kv__row--remark.apple-timeline-kv__row--between .v {
  flex: 1 1 0 !important;
  min-width: 0;
  text-align: left !important;
  white-space: pre-wrap;
}

.apple-timeline-kv__row--block .v {
  margin-top: 4px;
  padding-top: 8px;
  display: block;
  width: 100%;
  white-space: pre-wrap;
}

.history-empty-wrap {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.history-empty-wrap >>> .el-empty {
  width: 100%;
}

.history-empty-wrap >>> .el-empty__description {
  width: 100%;
  text-align: center;
}

.history-empty-wrap >>> .el-empty__description p {
  text-align: center;
}

.student-form .project-form-item {
  margin-bottom: 18px;
}

.student-contact-row + .student-contact-row {
  margin-top: 8px;
}

.contact-input-wrap {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 8px;
}

.contact-inline-label {
  width: 52px;
  flex: 0 0 52px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  height: 40px;
  line-height: 40px;
  color: #606266;
  font-size: 13px;
  white-space: nowrap;
}

.contact-inline-label-wechat {
  width: 90px;
  flex-basis: 90px;
}

.contact-inline-label.is-required::before {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

.contact-input-wrap .el-input {
  flex: 1;
}

.student-contact-actions-col {
  display: flex;
  justify-content: flex-end;
}

.student-contact-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.student-contact-add,
.student-contact-remove {
  font-size: 18px;
  cursor: pointer;
}

.student-contact-add {
  color: #67c23a;
}

.student-contact-remove {
  color: #f56c6c;
}

.student-contact-remove.disabled {
  cursor: not-allowed;
  color: #c0c4cc;
}

.project-checkbox-container {
  border: 1px solid #DCDFE6;
  border-radius: 4px;
  padding: 8px 12px;
  background: #fafafa;
}

.project-category {
  margin-bottom: 6px;
}

.project-category:last-child {
  margin-bottom: 0;
}

.category-title {
  color: #409EFF;
  font-size: 13px;
  margin-bottom: 4px;
  padding-left: 4px;
  border-left: 3px solid #409EFF;
  line-height: 1.4;
}

.category-items {
  padding-left: 2em;
}

.category-items .el-checkbox {
  margin-right: 12px;
  margin-bottom: 2px;
  line-height: 1.4;
}

.category-items .el-checkbox-group {
  line-height: 1.6;
}

.category-items .el-checkbox {
  margin-right: 20px;
  margin-bottom: 6px;
}

</style>

<style>

.push-form-dialog .el-form-item__label {
  white-space: nowrap;
}

.follow-form-dialog .el-dialog__body {
  overflow-x: hidden;
}

.follow-form-dialog .next-contact-control {
  display: flex;
  align-items: center;
  width: 100%;
}

.follow-form-dialog .next-contact-picker {
  flex: 1;
  max-width: 240px;
}

.follow-form-dialog .stop-follow-up-checkbox {
  flex: none;
  margin-left: 12px;
  white-space: nowrap;
}

.org-select-popper {
  width: 350px !important;
  max-width: 350px !important;
}

.org-select-popper .el-select-dropdown__list {
  max-width: 100%;
}

.org-select-popper .el-select-dropdown__item {
  display: flex;
  align-items: center;
  max-width: 100%;
}

.org-select-popper .org-option-text {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.org-select-popper .org-select-pager {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}

.org-select-popper .org-select-pager__text {
  color: #606266;
  font-size: 12px;
  white-space: nowrap;
}

</style>
